import React from "react";
import { Button } from "@/components/Calendar/ui/button";
import { Calendar } from "@/components/Calendar/ui/calendar";
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Clock,
  Filter as FilterIcon,
  RefreshCw,
  ExternalLink,
  Plus,
  CheckSquare,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  useScheduleData,
  type UseScheduleDataReturn,
  type Booking,
  type Task,
  type BlockOut,
  type BlockOutLane,
  getBlockOutLanes,
  getMemberByFullName,
  isMemberWorkingAt,
  isResourceAvailable,
  groupBookingsByDay,
  getDayShortName,
} from "@/components/Calendar/scheduleData";
import { ServiceTypeFilter } from "./schedule/ServiceTypeFilter";
import { PharmacistFilter } from "./schedule/PharmacistFilter";
import { RoomFilter } from "./schedule/RoomFilter";
import { BlockOutTypesFilter } from "./schedule/BlockOutTypesFilter";
import { ServiceTypesInfo } from "./schedule/ServiceTypesInfo";
import { ScheduleFilters } from "./schedule/ScheduleFilters";
import { PharmaciesFilter } from "./schedule/PharmaciesFilter";
import { ViewSettings } from "./schedule/ViewSettings";
import { SaveDefaultViewBar } from "./schedule/SaveDefaultViewBar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/Calendar/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/Calendar/ui/dropdown-menu";
import {
  saveSchedulePreferences,
  loadSchedulePreferences,
  type SchedulePreferences,
  mapApiDisplayMode,
  mapApiViewType,
  mapApiGroupBy,
  displayModeToApiMap,
  viewTypeToApiMap,
  groupByToApiMap,
} from "./schedule/schedulePreferences";
import { BookingModal } from "../Scheduler/schedule/BookingModal";
import { CalendarDayCell } from "./schedule/CalendarDayCell";
import { TimeSlotCell } from "./schedule/TimeSlotCell";
import { BlockOutModal } from "../Scheduler/schedule/BlockOutModal";
import { TaskModal } from "./schedule/TaskModal";
import { CalendarSyncModal } from "./schedule/CalendarSyncModal";
import ScheduleSkeleton from "./scheduleSkeleton";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  saveGoogleEvents,
  getGoogleEvents,
  getScheduleEventsByPharmacies,
  saveUserSettings,
  getGoogleCalendarStatus,
  refreshScheduleEventsSilently,
} from "@/dispatchers/scheduleEventsDispatchers";
import {
  getPharmacyAvailableSlots,
  getActivePharmacists,
} from "@/dispatchers/pharmacyServiceDispatchers";
import { getUsrAvailibility } from "@/dispatchers/scheduleEventsDispatchers";
import {
  showSuccess,
  showError,
} from "@/components/ToastedMessage/ToastedMessage";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/Calendar/ui/dialog";
import { Button as UIButton } from "@/components/Calendar/ui/button";
import { Switch } from "@/components/Calendar/ui/switch";
import { Label } from "@/components/Calendar/ui/label";

function getPractitionerInitials(fullName: string) {
  const parts = fullName.split(" ").filter(Boolean);
  return parts.map((part) => part.charAt(0).toUpperCase()).join("");
}

interface ScheduleProps {
  isDarkMode?: boolean;
  initialDateString?: string;
  onGoToPatient?: (patientName: string) => void;
}

interface GoogleCalendarSyncState {
  provider: "google";
  email: string;
  syncedAt: string;
  accessToken: string;
}

interface GoogleCalendarEvent {
  id: string;
  summary: string;
  description?: string;
  startDateTime?: string;
  endDateTime?: string;
  date: string;
  time: string;
  duration: number;
  htmlLink?: string;
  resourceId?: string;
  attendees?: Array<{
    email: string;
    self?: boolean;
    responseStatus?: string;
  }>;
}

const findResourceByName = (name: string, resourcesList: any[]) => {
  if (!resourcesList || !Array.isArray(resourcesList) || !name) return null;
  const normName = name.trim().toLowerCase();
  return (
    resourcesList.find((r: any) => {
      const matchName = r.name && r.name.trim().toLowerCase() === normName;
      const matchId = r.id && String(r.id).trim().toLowerCase() === normName;
      return matchName || matchId;
    }) || null
  );
};

// A Google event with no resourceId can't be attributed to anyone, so it stays visible everywhere.
const googleEventBelongsToPharmacist = (
  event: { resourceId?: string },
  pharmacistName: string,
  resourcesList: any[],
): boolean => {
  const normResourceId = String(event?.resourceId || "")
    .trim()
    .toLowerCase();
  if (!normResourceId) return true;

  const normP = (pharmacistName || "").trim().toLowerCase();
  if (normResourceId === normP) return true;

  const resource = findResourceByName(pharmacistName, resourcesList);
  if (!resource) return false;

  const resourceId = String(resource.id || "")
    .trim()
    .toLowerCase();
  const resourceEmail = String(
    (resource as any).userEmail || (resource as any).email || "",
  )
    .trim()
    .toLowerCase();

  return normResourceId === resourceId || normResourceId === resourceEmail;
};

const checkSlotWorkingStatus = (
  date: Date,
  timeString: string | undefined,
  columnMode: "practitioner" | "day" | "services" | "room" | "day-practitioner",
  columnValue: string,
  selectedPharmacists: string[],
  selectedRooms: string[],
  selectedServiceTypes: string[],
  resourcesList: any[],
  pharmacistNamesList: string[],
  roomNamesList: string[],
): boolean => {
  const dayOfWeek = date.getDay();

  if (columnMode === "practitioner" || columnMode === "day-practitioner") {
    const pharmacistName = columnValue;
    const resource = findResourceByName(pharmacistName, resourcesList);
    let isWorking = true;

    if (resource) {
      isWorking = isResourceAvailable(
        resource,
        dayOfWeek,
        timeString,
        selectedServiceTypes,
      );
    } else {
      const member = getMemberByFullName(pharmacistName);
      if (member) {
        isWorking = timeString
          ? isMemberWorkingAt(member, dayOfWeek, timeString)
          : true;
      } else {
        const activePharmacists =
          selectedPharmacists.length > 0
            ? selectedPharmacists
            : pharmacistNamesList;
        isWorking =
          activePharmacists.length === 0 ||
          activePharmacists.some((pName) => {
            const pRes = findResourceByName(pName, resourcesList);
            if (pRes) {
              return isResourceAvailable(
                pRes,
                dayOfWeek,
                timeString,
                selectedServiceTypes,
              );
            }
            const mem = getMemberByFullName(pName);
            return timeString
              ? isMemberWorkingAt(mem, dayOfWeek, timeString)
              : true;
          });
      }
    }

    // If specific room filter is active, check room availability as well
    if (isWorking && selectedRooms && selectedRooms.length > 0) {
      const isAnyRoomAvail = selectedRooms.some((roomName) => {
        const roomRes = findResourceByName(roomName, resourcesList);
        return isResourceAvailable(
          roomRes,
          dayOfWeek,
          timeString,
          selectedServiceTypes,
        );
      });
      if (!isAnyRoomAvail) {
        isWorking = false;
      }
    }

    return isWorking;
  }

  if (columnMode === "room") {
    const roomName = columnValue;
    const resource = findResourceByName(roomName, resourcesList);
    let isWorking = true;

    if (resource) {
      isWorking = isResourceAvailable(
        resource,
        dayOfWeek,
        timeString,
        selectedServiceTypes,
      );
    }

    // If specific pharmacist filter is active, check pharmacist availability as well
    if (isWorking && selectedPharmacists && selectedPharmacists.length > 0) {
      const isAnyPharmAvail = selectedPharmacists.some((pharmName) => {
        const pharmRes = findResourceByName(pharmName, resourcesList);
        return isResourceAvailable(
          pharmRes,
          dayOfWeek,
          timeString,
          selectedServiceTypes,
        );
      });
      if (!isAnyPharmAvail) {
        isWorking = false;
      }
    }

    return isWorking;
  }

  if (columnMode === "services") {
    const serviceType = columnValue;

    const activePharmacists =
      selectedPharmacists.length > 0
        ? selectedPharmacists
        : pharmacistNamesList;
    const activeRooms =
      selectedRooms.length > 0 ? selectedRooms : roomNamesList;

    const pharmAvail =
      activePharmacists.length === 0 ||
      activePharmacists.some((pName) => {
        const pRes = findResourceByName(pName, resourcesList);
        return isResourceAvailable(pRes, dayOfWeek, timeString, [serviceType]);
      });

    const roomAvail =
      activeRooms.length === 0 ||
      activeRooms.some((rName) => {
        const rRes = findResourceByName(rName, resourcesList);
        return isResourceAvailable(rRes, dayOfWeek, timeString, [serviceType]);
      });

    return pharmAvail && roomAvail;
  }

  // columnMode === "day" or Month View
  const activePharmacists =
    selectedPharmacists.length > 0 ? selectedPharmacists : pharmacistNamesList;
  const activeRooms = selectedRooms.length > 0 ? selectedRooms : roomNamesList;

  const pharmAvail =
    activePharmacists.length === 0 ||
    activePharmacists.some((pName) => {
      const pRes = findResourceByName(pName, resourcesList);
      return isResourceAvailable(
        pRes,
        dayOfWeek,
        timeString,
        selectedServiceTypes,
      );
    });

  const roomAvail =
    activeRooms.length === 0 ||
    activeRooms.some((rName) => {
      const rRes = findResourceByName(rName, resourcesList);
      return isResourceAvailable(
        rRes,
        dayOfWeek,
        timeString,
        selectedServiceTypes,
      );
    });

  return pharmAvail && roomAvail;
};

export default function ScheduleCalendar({
  initialDateString,
  onGoToPatient,
}: ScheduleProps) {
  const s = useScheduleData();
  const routerNavigate = useNavigate();

  // Destructure everything we use from the hook result, with safe defaults
  const {
    isDarkMode = false,
    // theme / core
    isEventsLoading = false,
    isResourcesLoading = false,
    isScheduleEventsByPharmaciesLoading = false,
    companyId = "",
    currentCompanyId = "",
    ifCalendarIntegrationEnabled = false,
    userDefaultCalendarView = null,
    currentDate = new Date(),
    view = "month",
    setView = () => {},
    calendarSelectedDate = new Date(),
    modalState = {
      isOpen: false,
      mode: null,
    } as UseScheduleDataReturn["modalState"],
    blockOutModalState = {
      isOpen: false,
      mode: null,
    } as UseScheduleDataReturn["blockOutModalState"],

    // data / labels
    dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    timeSlots = [],
    serviceColors = { Earwax: "", Travel: "", Weight: "", Covid: "" },
    serviceColorsFromData = {},
    serviceColorMap = {}, // API service colors (hex)
    serviceNameMap = {}, // Service ID to name mapping
    pharmacists = [],
    pharmacistNames = [],
    dynamicRooms = [],
    pharmacistResources: resources = [],
    roomNames = [],
    serviceTypes = [],
    defaultPharmacyServices = [],
    dynamicResources,
    serviceTypesInfo = [],
    members = [],
    clinicians = [],
    currentUserEmail = "",
    handleStartConsultation,

    // filters state
    selectedServiceTypes = [],
    selectedPharmacists = [],
    selectedRooms = [],
    selectedBlockOutTypes = [],
    currentPharmacyId = "",
    googleSyncEvents = [],
    googleCalendarStatus,
    isGoogleCalendarSyncing = false,
    pharmacyRooms = [],
    allUserWeekAvailability = [],
    // utils
    formatDate = (y: number, m: number, d: number) =>
      `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`,
    formatDateFromDate = (date: Date) =>
      `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
        2,
        "0",
      )}-${String(date.getDate()).padStart(2, "0")}`,
    getViewTitle = () => "",
    getDaysInMonth = () => [],
    getWeekDays = () => [],
    getThreeDays = () => [],
    getWorkWeekDays = () => [],
    getFilteredBookingsForDate = () => [],
    getFilteredBookingsForTimeSlot = () => [],
    getFilteredBlockOutsForDate = () => [],
    getFilteredBlockOutsForTimeSlot = () => [],
    filteredTasks = [],
    getFilteredTasksForDate,
    getFilteredTasksForTimeSlot,
    validateBookingConflicts,

    // actions
    navigate = () => {},
    onCalendarSelect = () => {},
    handleViewDay = () => {},
    onCreateBooking = () => {},
    onCreateBookingForDate = () => {},
    onCreateBookingWithDefaults = () => {},
    onShowEditBooking = () => {},
    onEditBooking = () => {},
    onDeleteBooking = () => {},
    onCreateBlockOut = () => {},
    onShowEditBlockOut = () => {},
    onEditBlockOut = () => {},
    onDeleteBlockOut = () => {},
    onSaveFromModal = () => {},
    closeModal = () => {},
    closeBlockOutModal = () => {},
    onSaveFromBlockOutModal = () => {},
    openNewAppointment = () => {},

    // conflict warning
    conflictWarning = null,
    clearConflictWarning = () => {},

    // filter handlers
    handleServiceTypeChange = () => {},
    handleSelectAllServices = () => {},
    handlePharmacistChange = () => {},
    handleSelectAllPharmacists = () => {},
    handleRoomChange = () => {},
    handleSelectAllRooms = () => {},
    handleBlockOutTypeChange = () => {},
    handleSelectAllBlockOutTypes = () => {},
    pharmacies = [],
    selectedPharmacies = [],
    handlePharmacyChange = () => {},
    handleSelectAllPharmacies = () => {},
    setSelectedPharmacists = () => {},
    setSelectedServiceTypes = () => {},
    setSelectedRooms = () => {},
    setSelectedPharmacies = () => {},
  } = (s || {}) as Partial<UseScheduleDataReturn>;

  // The calendar is mounted below the app header inside an overflow-hidden
  // panel, so a plain `h-screen` root pushes its bottom edge (and the list
  // scroll bar) below the visible area. Size it to the space that is left.
  const rootRef = useRef<HTMLDivElement>(null);
  const [availableHeight, setAvailableHeight] = useState<number | null>(null);
  const isCalendarLoading = isEventsLoading || isResourcesLoading;
  const isMobile = useIsMobile();

  useLayoutEffect(() => {
    const updateAvailableHeight = () => {
      const root = rootRef.current;
      if (!root) return;
      const { top } = root.getBoundingClientRect();
      setAvailableHeight(Math.max(window.innerHeight - top, 0));
    };
    updateAvailableHeight();
    window.addEventListener("resize", updateAvailableHeight);
    return () => window.removeEventListener("resize", updateAvailableHeight);
  }, [isCalendarLoading]);
  const [displayMode, setDisplayMode] = useState<"calendar" | "list">(
    "calendar",
  );
  const [columnMode, setColumnMode] = useState<
    "practitioner" | "day" | "services" | "room" | "day-practitioner"
  >("day-practitioner");
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [showBlockOutModal, setShowBlockOutModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [rescheduleData, setRescheduleData] = useState<{
    booking: Booking;
    newDate: string;
    newTime?: string;
    newPharmacist?: string;
    newRoom?: string;
  } | null>(null);
  const [notifyPatient, setNotifyPatient] = useState(true);
  const [notifiedBookingId, setNotifiedBookingId] = useState<string | null>(
    null,
  );
  // Pulses a booking's own service-color border - used to point out the
  // specific appointment a dashboard "appointment" click navigated to.
  const [highlightedAppointmentId, setHighlightedAppointmentId] = useState<
    string | null
  >(null);
  const [rescheduleApiMessage, setRescheduleApiMessage] = useState<
    string | null
  >(null);
  const [
    isCheckingRescheduleAvailability,
    setIsCheckingRescheduleAvailability,
  ] = useState(false);
  const [isSubmittingReschedule, setIsSubmittingReschedule] = useState(false);
  const lastRescheduleAvailabilityKeyRef = React.useRef<string | null>(null);
  const [notificationType, setNotificationType] = useState<"message" | "check">(
    "check",
  );
  const [showSaveDefaultBar, setShowSaveDefaultBar] = useState(false);
  const [isSavingDefaultView, setIsSavingDefaultView] = useState(false);
  const [initialPreferences, setInitialPreferences] =
    useState<SchedulePreferences | null>(null);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);

  // Populated when this mount was triggered by a dashboard "appointment"
  // click - see useDashboardData.onAppointmentClick. Forces day view at that
  // date instead of applying the user's saved default view, and highlights
  // that specific appointment once its events have loaded.
  const targetAppointmentRef = useRef<{
    date: string;
    appointmentId: string | null;
  } | null>(null);
  const targetAppointmentOpenedRef = useRef(false);


  const getBookingsForDate = (dateString: string) =>
    getFilteredBookingsForDate(dateString);

  const getBookingsForTimeSlot = (dateString: string, timeSlot: number) => {
    const slotMinutes = Math.round(timeSlot * 60);

    return getFilteredBookingsForTimeSlot(dateString, timeSlot).filter(
      (booking) => {
        const [bookingHour, bookingMinute] = (booking.time || "00:00")
          .split(":")
          .map(Number);
        const bookingStart = (bookingHour || 0) * 60 + (bookingMinute || 0);
        return bookingStart >= slotMinutes && bookingStart < slotMinutes + 30;
      },
    );
  };

  const mapGoogleApiEvent = (item: any): GoogleCalendarEvent => {
    const start = item.start?.dateTime || item.start?.date;
    const end = item.end?.dateTime || item.end?.date;
    const startDate = start ? new Date(start) : new Date();
    const endDate = end
      ? new Date(end)
      : new Date(startDate.getTime() + 60 * 60000);
    const duration = Math.max(
      30,
      Math.round((endDate.getTime() - startDate.getTime()) / 60000),
    );
    const allDay = !!item.start?.date && !item.start?.dateTime;
    const time = allDay
      ? "All day"
      : `${String(startDate.getHours()).padStart(2, "0")}:${String(
          startDate.getMinutes(),
        ).padStart(2, "0")}`;

    return {
      id: item.id,
      summary: item.summary || "Untitled event",
      description: item.description,
      startDateTime: item.start?.dateTime,
      endDateTime: item.end?.dateTime,
      date: `${startDate.getFullYear()}-${String(
        startDate.getMonth() + 1,
      ).padStart(2, "0")}-${String(startDate.getDate()).padStart(2, "0")}`,
      time,
      duration,
      htmlLink: item.htmlLink,
      attendees: item.attendees || [],
    };
  };

  /**
   * Filter Google Calendar events based on attendee email matching resourceId
   * @param events - Array of Google Calendar events to filter
   * @param resourceId - Email address to match against attendees (typically user's email)
   * @returns Filtered events where the resourceId email is in the attendees list
   */
  const filterEventsByResourceId = (
    events: GoogleCalendarEvent[],
    resourceId: string,
  ): GoogleCalendarEvent[] => {
    const normalizedResourceId = (resourceId || "").trim().toLowerCase();

    if (!normalizedResourceId) return [];

    return events
      .map((event) => {
        const matchingAttendees = (event.attendees || []).filter((attendee) => {
          const attendeeEmail = (attendee.email || "").trim().toLowerCase();
          return attendeeEmail === normalizedResourceId;
        });

        if (matchingAttendees.length === 0) {
          return null;
        }

        return {
          ...event,
          attendees: matchingAttendees,
        };
      })
      .filter(Boolean) as GoogleCalendarEvent[];
  };

  /**
   * Get Google Calendar events for a specific time slot
   * @param dateString - Date in format YYYY-MM-DD
   * @param timeSlot - Hour in 24-hour format
   * @returns Google Calendar events that overlap with the time slot
   */
  const getGoogleEventsForTimeSlot = (dateString: string, timeSlot: number) => {
    const hour = Math.floor(timeSlot);
    const minutes = timeSlot % 1 === 0 ? 0 : 30;

    return googleCalendarEvents.filter((event) => {
      // Match by date
      if (event.date !== dateString) return false;

      // Extract time from event.time (format: "HH:mm")
      if (event.time === "All day") return false;

      const [eventHour, eventMinutes = 0] = event.time.split(":").map(Number);
      return eventHour === hour && eventMinutes === minutes;
    });
  };

  /**
   * Organize Google Calendar events by date for week view
   * @param dates - Array of date strings in format YYYY-MM-DD
   * @returns Object with dates as keys and arrays of events as values
   */
  const getWeekGoogleEventsByDate = (
    dates: string[],
  ): Record<string, GoogleCalendarEvent[]> => {
    const eventsByDate: Record<string, GoogleCalendarEvent[]> = {};
    dates.forEach((dateString) => {
      eventsByDate[dateString] = googleCalendarEvents.filter(
        (event) => event.date === dateString,
      );
    });
    return eventsByDate;
  };

  // Get Google Calendar events for a specific date (month view)
  const getDayGoogleEvents = (dateString: string): GoogleCalendarEvent[] =>
    googleCalendarEvents.filter((event) => event.date === dateString);

  // Fetch Google Calendar status on mount and handle OAuth redirect query params
  useEffect(() => {
    getGoogleCalendarStatus();

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const isConnected = urlParams.get("connected") === "true";
      const googleError = urlParams.get("google_error");
      const emailParam = urlParams.get("email");

      if (isConnected) {
        showSuccess(
          emailParam
            ? `Google Calendar connected with ${emailParam}!`
            : "Google Calendar connected successfully!",
        );
        getGoogleCalendarStatus();
        // Connecting only links the account - no automatic sync into GoogleEvents here either.
        if (currentPharmacyId) {
          refreshScheduleEventsSilently(currentPharmacyId);
        }
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      } else if (googleError) {
        showError(`Google Calendar connection failed: ${googleError}`);
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      }
    } catch (e) {
      // ignore
    }
  }, [currentPharmacyId]);

  // Generate calendar time slots for the full 24 hours (48 half-hour slots); the
  // view scrolls so 08:00 starts at the top, earlier hours are reachable by scrolling up.
  const weekTimeSlots = React.useMemo(() => {
    return Array.from({ length: 48 }, (_, i) => i * 0.5);
  }, []);

  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (displayMode !== "calendar") return;
    if (
      view !== "day" &&
      view !== "week" &&
      view !== "threeday" &&
      view !== "workweek"
    ) {
      return;
    }

    const scrollToEight = () => {
      const container = scrollContainerRef.current;
      if (!container) return;

      const target = container.querySelector<HTMLElement>(
        '[data-timeslot="8"]',
      );
      if (target) {
        const header = container.querySelector<HTMLElement>(
          '[data-calendar-header="true"]',
        );
        const headerHeight = header ? header.offsetHeight : 0;
        const targetTop =
          target.getBoundingClientRect().top -
          container.getBoundingClientRect().top +
          container.scrollTop;
        container.scrollTop = Math.max(0, targetTop - headerHeight);
      }
    };

    scrollToEight();
    const rafId = requestAnimationFrame(scrollToEight);
    const timer = setTimeout(scrollToEight, 100);

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timer);
    };
  }, [view, displayMode, columnMode, isEventsLoading, isResourcesLoading]);

  // Use Redux-backed Google sync events for display (populated by getGoogleEvents),
  // narrowed down to the currently selected pharmacist filter by resourceId - same
  // matching approach (name or resource id) used for bookings/tasks filtering above.
  const googleCalendarEvents: GoogleCalendarEvent[] = React.useMemo(() => {
    const events: GoogleCalendarEvent[] = googleSyncEvents || [];
    if (!selectedPharmacists || selectedPharmacists.length === 0) {
      return events;
    }
    return events.filter((event) => {
      const normResourceId = String(event.resourceId || "")
        .trim()
        .toLowerCase();
      if (!normResourceId) return true;

      return selectedPharmacists.some((pName) => {
        const normP = (pName || "").trim().toLowerCase();
        if (normResourceId === normP) return true;

        const resource = findResourceByName(pName, resources);
        if (!resource) return false;

        const resourceId = String(resource.id || "")
          .trim()
          .toLowerCase();
        const resourceEmail = String(
          (resource as any).userEmail || (resource as any).email || "",
        )
          .trim()
          .toLowerCase();

        return (
          normResourceId === resourceId || normResourceId === resourceEmail
        );
      });
    });
  }, [googleSyncEvents, selectedPharmacists, resources]);

  const formatTimeSlotLabel = (timeSlot: number) => {
    const hour = Math.floor(timeSlot);
    const minutes = timeSlot % 1 === 0 ? 0 : 30;

    return `${hour.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}`;
  };

  // Load default calendar view preferences from API data/settings, unless a
  // dashboard "appointment" click asked for a specific date in day view
  // instead - read fresh from localStorage here (not the URL, so the address
  // bar stays clean) so this is the single place deciding the initial view.
  useEffect(() => {
    // Already forced into day view for a requested appointment on a previous
    // run of this effect (e.g. userDefaultCalendarView arriving later) -
    // don't let that overwrite it back to the saved default.
    if (targetAppointmentRef.current) return;

    let requestedDate: string | null = null;
    let requestedAppointmentId: string | null = null;
    try {
      if (localStorage.getItem("calendarFromAppointment") === "1") {
        requestedDate = localStorage.getItem("calendarRequestedDate");
        requestedAppointmentId = localStorage.getItem(
          "calendarRequestedAppointmentId",
        );
      }
    } catch {}
    try {
      localStorage.removeItem("calendarFromAppointment");
      localStorage.removeItem("calendarRequestedDate");
      localStorage.removeItem("calendarRequestedView");
      localStorage.removeItem("calendarRequestedAppointmentId");
    } catch {}

    if (requestedDate) {
      targetAppointmentRef.current = {
        date: requestedDate,
        appointmentId: requestedAppointmentId,
      };

      handleViewDay(requestedDate);
      setColumnMode("day-practitioner");
      setDisplayMode("calendar");
      setInitialPreferences({
        view: "day",
        columnMode: "day-practitioner",
        displayMode: "calendar",
        selectedServiceTypes: [],
        selectedPharmacists: [],
        selectedRooms: [],
        selectedBlockOutTypes: [],
        selectedPharmacies: [],
      });
      setPreferencesLoaded(true);
      setShowSaveDefaultBar(false);
      return;
    }

    const rawDefaultView =
      userDefaultCalendarView?.defaultCalendarView || userDefaultCalendarView;
    const rawFilters = userDefaultCalendarView?.calendarFilters;

    const savedView = rawDefaultView?.viewType
      ? mapApiViewType(rawDefaultView.viewType)
      : "workweek";
    // Multi-day time grids are unusable at phone width, so start on Day there.
    // Month still works, so a saved Month default is kept.
    const defaultView =
      isMobile && savedView !== "month" ? "day" : savedView;
    const defaultColumnMode = rawDefaultView?.groupBy
      ? mapApiGroupBy(rawDefaultView.groupBy)
      : "day-practitioner";
    const defaultDisplayMode = rawDefaultView?.displayMode
      ? mapApiDisplayMode(rawDefaultView.displayMode)
      : "calendar";

    setView(defaultView);
    setColumnMode(defaultColumnMode);
    setDisplayMode(defaultDisplayMode);

    const initialServices: string[] = Array.isArray(rawFilters?.serviceTypeIds)
      ? rawFilters.serviceTypeIds
          .map((sId: string) => {
            const match = (defaultPharmacyServices || []).find(
              (s: any) =>
                s._id === sId ||
                s.id === sId ||
                s.serviceName === sId ||
                s.name === sId,
            );
            return match ? match._id || match.serviceName || match.name : sId;
          })
          .filter(Boolean)
      : [];
    if (rawFilters && rawFilters.serviceTypeIds !== undefined) {
      setSelectedServiceTypes(initialServices);
    }

    const currentUserResource = (resources || []).find(
      (r: any) =>
        currentUserEmail &&
        r.id === currentUserEmail &&
        (r.resourceType === 1 || !r.resourceType),
    );
    const currentUserResName = currentUserResource?.name || "";

    let initialPharmacists: string[] = [];
    if (
      Array.isArray(rawFilters?.pharmacistIds) &&
      rawFilters.pharmacistIds.length > 0
    ) {
      initialPharmacists = rawFilters.pharmacistIds
        .map((pId: string) => {
          const match = (resources || []).find(
            (r: any) =>
              (r.resourceType === 1 || !r.resourceType) &&
              (r.id === pId || r.name === pId),
          );
          return match ? match.name : pId;
        })
        .filter(Boolean);
      setSelectedPharmacists(initialPharmacists);
    } else if (
      rawFilters &&
      Array.isArray(rawFilters.pharmacistIds) &&
      rawFilters.pharmacistIds.length === 0
    ) {
      const defaultUser = currentUserResName ? [currentUserResName] : [];
      initialPharmacists = defaultUser;
      setSelectedPharmacists(defaultUser);
    } else if (currentUserResName) {
      initialPharmacists = [currentUserResName];
      setSelectedPharmacists([currentUserResName]);
    }

    const initialRooms: string[] = Array.isArray(rawFilters?.roomIds)
      ? rawFilters.roomIds
          .map((rId: string) => {
            const match = (resources || []).find(
              (r: any) =>
                r.resourceType === 2 && (r.id === rId || r.name === rId),
            );
            return match ? match.name : rId;
          })
          .filter(Boolean)
      : [];
    if (rawFilters && rawFilters.roomIds !== undefined) {
      setSelectedRooms(initialRooms);
    }

    const initialPharmacies: string[] = Array.isArray(rawFilters?.pharmacyIds)
      ? rawFilters.pharmacyIds.map((pId: any) => String(pId)).filter(Boolean)
      : [];
    if (rawFilters && rawFilters.pharmacyIds !== undefined) {
      setSelectedPharmacies(initialPharmacies);
    }

    setInitialPreferences({
      view: defaultView,
      columnMode: defaultColumnMode,
      displayMode: defaultDisplayMode,
      selectedServiceTypes: initialServices,
      selectedPharmacists: initialPharmacists,
      selectedRooms: initialRooms,
      selectedBlockOutTypes: [],
      selectedPharmacies: initialPharmacies,
    });

    setPreferencesLoaded(true);
    setShowSaveDefaultBar(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDefaultCalendarView, resources, defaultPharmacyServices, currentUserEmail]);

  // Once that date's events have loaded, highlight (pulse its own service
  // color) the specific appointment that was clicked on the dashboard - not
  // just the right day - without popping its edit modal open.
  useEffect(() => {
    const request = targetAppointmentRef.current;
    if (
      !request?.appointmentId ||
      targetAppointmentOpenedRef.current ||
      isCalendarLoading
    ) {
      return;
    }

    const match = getFilteredBookingsForDate(request.date).find(
      (booking) => String(booking.id) === String(request.appointmentId),
    );
    if (match) {
      targetAppointmentOpenedRef.current = true;
      setHighlightedAppointmentId(match.id);
      setTimeout(() => setHighlightedAppointmentId(null), 6000);
    }
  }, [isCalendarLoading, getFilteredBookingsForDate]);

  useEffect(() => {
    if (initialDateString) {
      const d = new Date(initialDateString);
      if (!isNaN(d.getTime())) {
        onCalendarSelect(d);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDateString]);

  // Helper to compare two string arrays ignoring order
  const areArraysEqual = (a: string[] = [], b: string[] = []) => {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((val, idx) => val === sortedB[idx]);
  };

  // Detect changes to view settings & filters and show save bar
  useEffect(() => {
    if (!preferencesLoaded || !initialPreferences) return;

    // Check if view settings differ from initial / saved preferences
    const viewChanged =
      view !== initialPreferences.view ||
      columnMode !== initialPreferences.columnMode ||
      displayMode !== initialPreferences.displayMode;

    const filtersChanged =
      !areArraysEqual(
        selectedServiceTypes,
        initialPreferences.selectedServiceTypes,
      ) ||
      !areArraysEqual(
        selectedPharmacists,
        initialPreferences.selectedPharmacists,
      ) ||
      !areArraysEqual(selectedRooms, initialPreferences.selectedRooms) ||
      !areArraysEqual(
        selectedPharmacies,
        initialPreferences.selectedPharmacies,
      );

    const hasChanged = viewChanged || filtersChanged;

    setShowSaveDefaultBar(hasChanged);
  }, [
    preferencesLoaded,
    initialPreferences,
    view,
    columnMode,
    displayMode,
    selectedServiceTypes,
    selectedPharmacists,
    selectedRooms,
    selectedPharmacies,
  ]);

  const handleSaveDefaultView = async () => {
    const preferences: SchedulePreferences = {
      view,
      columnMode,
      displayMode,
      selectedServiceTypes,
      selectedPharmacists,
      selectedRooms,
      selectedBlockOutTypes,
      selectedPharmacies,
    };

    const targetCompanyId = currentCompanyId || companyId || "";

    const serviceTypeIds = (selectedServiceTypes || [])
      .map((st) => {
        const match = (defaultPharmacyServices || []).find(
          (s: any) =>
            s._id === st ||
            s.id === st ||
            s.serviceName === st ||
            s.name === st,
        );
        return match ? match._id || match.id : st;
      })
      .filter(Boolean);

    const pharmacistIds = (selectedPharmacists || [])
      .map((pName) => {
        const match = (resources || []).find(
          (r: any) =>
            (r.resourceType === 1 || !r.resourceType) &&
            (r.name === pName || r.id === pName),
        );
        return match ? match.id : pName;
      })
      .filter(Boolean);

    const roomIds = (selectedRooms || [])
      .map((rName) => {
        const match = (resources || []).find(
          (r: any) =>
            r.resourceType === 2 && (r.name === rName || r.id === rName),
        );
        return match ? match.id : rName;
      })
      .filter(Boolean);

    const pharmacyIds = (selectedPharmacies || [])
      .map((pId) => String(pId))
      .filter(Boolean);

    const payload = {
      companyId: targetCompanyId,
      companyGuidId: targetCompanyId,
      defaultCalendarView: {
        displayMode: displayModeToApiMap[displayMode] || 1,
        viewType: viewTypeToApiMap[view] || 3,
        groupBy: groupByToApiMap[columnMode] || 3,
      },
      calendarFilters: {
        serviceTypeIds,
        pharmacistIds,
        roomIds,
        pharmacyIds,
      },
    };

    setIsSavingDefaultView(true);
    try {
      await saveUserSettings(payload);
      saveSchedulePreferences(preferences);
      setInitialPreferences(preferences);
      setShowSaveDefaultBar(false);
    } catch (error) {
      console.error("Failed to save default user settings:", error);
    } finally {
      setIsSavingDefaultView(false);
    }
  };

  const handleDismissSaveBar = () => {
    setShowSaveDefaultBar(false);
  };

  const handleBookingDrop = (
    booking: Booking,
    newDate: string,
    newTime?: string,
    newPharmacist?: string,
    newRoom?: string,
  ) => {
    const targetTime = newTime || booking.time;
    const targetPharmacist = newPharmacist || booking.pharmacist;
    const targetRoom = newRoom || booking.room;

    if (
      booking.date === newDate &&
      booking.time === targetTime &&
      booking.pharmacist === targetPharmacist &&
      booking.room === targetRoom
    ) {
      return;
    }
    setNotifyPatient(true); // Reset to default (on)
    setRescheduleData({
      booking,
      newDate,
      newTime: targetTime,
      newPharmacist: targetPharmacist,
      newRoom: targetRoom,
    });
  };

  React.useEffect(() => {
    if (!rescheduleData) {
      lastRescheduleAvailabilityKeyRef.current = null;
      setRescheduleApiMessage(null);
      setIsCheckingRescheduleAvailability(false);
      return;
    }

    const { booking, newDate, newTime, newPharmacist, newRoom } =
      rescheduleData;
    const newDateIso = new Date(newDate).toISOString();
    const availabilityKey = [
      booking.id || (booking as any)._id || "",
      newDateIso,
      newTime || booking.time || "",
      newPharmacist || booking.pharmacist || "",
      newRoom || booking.room || "",
    ].join("|");

    if (lastRescheduleAvailabilityKeyRef.current === availabilityKey) {
      return;
    }

    lastRescheduleAvailabilityKeyRef.current = availabilityKey;
    setIsCheckingRescheduleAvailability(true);

    let isMounted = true;
    setRescheduleApiMessage(null);

    const runRescheduleAvailabilityCheck = async () => {
      const targetTime = newTime || booking.time;
      const targetPharmacist = newPharmacist || booking.pharmacist;
      const targetRoom = newRoom || booking.room;
      const targetDuration = Number(booking.duration || 30);
      let apiAvailabilityMessage: string | null = null;

      try {
        const pharmacyId = String(
          (booking as any)?.otherPharmacyId || currentPharmacyId || "",
        ).trim();
        const serviceId =
          String(booking.serviceId || "").trim() ||
          (
            defaultPharmacyServices.find(
              (service: any) =>
                String(service.serviceName || "") ===
                  String(booking.serviceType || "") ||
                String(service._id || "") === String(booking.serviceType || ""),
            ) as any
          )?._id ||
          "";
        const resolvedResource =
          (resources || []).find(
            (resource: any) =>
              String(resource?.id || "") === String(targetPharmacist) ||
              String(resource?.name || "") === String(targetPharmacist) ||
              String(resource?.userEmail || "") === String(targetPharmacist),
          ) || null;
        const resourceIdentifier =
          resolvedResource?.id ||
          resolvedResource?.userEmail ||
          resolvedResource?.email ||
          targetPharmacist;
        let activePharmacistsResponse: any = null;
        if (pharmacyId && newDate && targetDuration > 0) {
          await getPharmacyAvailableSlots({
            pharmacyId,
            appointmentDate: newDateIso,
            duration: targetDuration,
            ...(booking.id ? { eventId: String(booking.id) } : {}),
          });
        }

        if (resourceIdentifier && serviceId && pharmacyId) {
          await getUsrAvailibility(resourceIdentifier, serviceId, pharmacyId);
        }

        if (
          serviceId &&
          pharmacyId &&
          newDate &&
          targetDuration > 0 &&
          targetTime
        ) {
          activePharmacistsResponse = await getActivePharmacists({
            companyId,
            pharmacyId,
            serviceId,
            appointmentDate: newDateIso,
            duration: targetDuration,
            startTime: targetTime,
            ...(booking.id ? { eventId: String(booking.id) } : {}),
          });

          const activePharmacists = Array.isArray(activePharmacistsResponse)
            ? activePharmacistsResponse
            : activePharmacistsResponse?.activePharmacists ||
              activePharmacistsResponse?.users ||
              activePharmacistsResponse?.data ||
              [];
          const activePharmacist = activePharmacists.find(
            (pharmacist: any) =>
              String(
                pharmacist?.id ||
                  pharmacist?.emailAddress ||
                  pharmacist?.userEmail ||
                  "",
              ) === String(resourceIdentifier) ||
              String(pharmacist?.name || "")
                .trim()
                .toLowerCase() ===
                String(targetPharmacist || "")
                  .trim()
                  .toLowerCase(),
          );

          if (
            activePharmacist?.isOutsideWorkingHours ||
            activePharmacist?.isAvailable === false
          ) {
            apiAvailabilityMessage = `${
              activePharmacist.name ||
              resolvedResource?.name ||
              targetPharmacist
            } is not scheduled to work at this date and time based on their working hours.`;
          }
        }
      } catch (error) {
        console.error("Failed to refresh reschedule availability:", error);
      }

      if (!isMounted) return;

      setIsCheckingRescheduleAvailability(false);

      const validation = apiAvailabilityMessage
        ? { isValid: false, message: apiAvailabilityMessage }
        : validateBookingConflicts
          ? validateBookingConflicts(
              {
                date: newDate,
                time: targetTime,
                duration: String(targetDuration),
                pharmacist: targetPharmacist,
                room: targetRoom,
              },
              booking.id || (booking as any)._id,
            )
          : { isValid: true, message: "" };

      setRescheduleApiMessage(validation.isValid ? null : validation.message);
    };

    runRescheduleAvailabilityCheck();

    return () => {
      isMounted = false;
    };
  }, [rescheduleData]);

  const getPharmacistAvailabilityWarning = React.useCallback(
    (dateString: string, timeString: string, pharmacistName?: string) => {
      if (!pharmacistName || !timeString) return null;

      const normalizedPharmacist = String(pharmacistName).trim();
      if (!normalizedPharmacist || normalizedPharmacist === "any") return null;

      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return null;

      const targetResource =
        (resources || []).find(
          (resource: any) =>
            String(resource?.id || "") === String(normalizedPharmacist) ||
            String(resource?.name || "") === String(normalizedPharmacist) ||
            String(resource?.userEmail || "") === String(normalizedPharmacist),
        ) || null;

      const resourceToCheck =
        targetResource ||
        findResourceByName(normalizedPharmacist, resources || []);
      if (!resourceToCheck) return null;

      const isAvailable = isResourceAvailable(
        resourceToCheck,
        date.getDay(),
        timeString,
        undefined,
        allUserWeekAvailability,
      );

      if (!isAvailable) {
        return `${resourceToCheck.name || normalizedPharmacist} is not scheduled to work at this date and time based on their working hours. You can still create an ad-hoc appointment by selecting Create Appointment.`;
      }

      return null;
    },
    [resources, allUserWeekAvailability],
  );

  const rescheduleConflictMessage = React.useMemo(() => {
    if (!rescheduleData) return null;
    if (isCheckingRescheduleAvailability) {
      return null;
    }
    if (rescheduleApiMessage) {
      return rescheduleApiMessage;
    }
    const { booking, newDate, newTime, newPharmacist, newRoom } =
      rescheduleData;
    const targetTime = newTime || booking.time;
    const targetPharmacist = newPharmacist || booking.pharmacist;
    const targetRoom = newRoom || booking.room;
    const targetDuration = String(booking.duration || 30);

    const pharmacistAvailabilityWarning = getPharmacistAvailabilityWarning(
      newDate,
      targetTime,
      targetPharmacist,
    );
    if (pharmacistAvailabilityWarning) {
      return pharmacistAvailabilityWarning;
    }

    if (validateBookingConflicts) {
      const res = validateBookingConflicts(
        {
          date: newDate,
          time: targetTime,
          duration: targetDuration,
          pharmacist: targetPharmacist,
          room: targetRoom,
        },
        booking.id || (booking as any)._id,
      );
      if (!res.isValid) {
        return res.message;
      }
    }
    return null;
  }, [
    rescheduleData,
    rescheduleApiMessage,
    isCheckingRescheduleAvailability,
    validateBookingConflicts,
    getPharmacistAvailabilityWarning,
  ]);

  const handleConfirmReschedule = async () => {
    if (
      isSubmittingReschedule ||
      isCheckingRescheduleAvailability ||
      rescheduleConflictMessage
    ) {
      return;
    }
    if (rescheduleData) {
      setIsSubmittingReschedule(true);
      const targetTime = rescheduleData.newTime || rescheduleData.booking.time;
      const targetPharmacistRaw =
        rescheduleData.newPharmacist || rescheduleData.booking.pharmacist;
      const targetPharmacist =
        findResourceByName(targetPharmacistRaw, resources)?.id ||
        targetPharmacistRaw;
      const targetRoom = rescheduleData.newRoom || rescheduleData.booking.room;
      const updatedBooking = {
        ...rescheduleData.booking,
        date: rescheduleData.newDate,
        time: targetTime,
        resourceId: targetPharmacist,
        room: targetRoom,
        duration: rescheduleData.booking.duration.toString(),
        IsNotify: Boolean(notifyPatient),
      };

      try {
        await onEditBooking(rescheduleData.booking.id, updatedBooking);
        // Trigger notification animation
        setNotificationType(notifyPatient ? "message" : "check");
        setNotifiedBookingId(rescheduleData.booking.id);
        handleCancelReschedule();
        setTimeout(() => {
          setNotifiedBookingId(null);
        }, 2000);

        // setRescheduleData(null);
      } catch (e) {
        console.error("Failed to confirm reschedule:", e);
      } finally {
        setIsSubmittingReschedule(false);
      }
    }
  };

  const handleCancelReschedule = () => {
    setRescheduleData(null);
  };

  // Determine which pharmacists to display in columns
  // If filters are selected, use those; otherwise, default to all pharmacist names (dynamic)
  const displayPharmacists = React.useMemo(() => {
    if (selectedPharmacists.length > 0) {
      return selectedPharmacists;
    }
    // Default to all pharmacist names when no filters are selected
    return pharmacistNames;
  }, [selectedPharmacists, pharmacistNames]);

  const displayServices = React.useMemo(() => {
    if (selectedServiceTypes.length > 0) {
      return selectedServiceTypes.map((serviceIdOrName) => {
        return (
          serviceNameMap[serviceIdOrName] ||
          (serviceTypesInfo || []).find(
            (s: any) =>
              String(s._id) === String(serviceIdOrName) ||
              String(s.name).toLowerCase() ===
                String(serviceIdOrName).toLowerCase() ||
              String(s.label).toLowerCase() ===
                String(serviceIdOrName).toLowerCase(),
          )?.name ||
          (defaultPharmacyServices || []).find(
            (s: any) =>
              String(s._id || s.serviceID) === String(serviceIdOrName) ||
              String(s.serviceName).toLowerCase() ===
                String(serviceIdOrName).toLowerCase(),
          )?.serviceName ||
          serviceIdOrName
        );
      });
    }
    return serviceTypes;
  }, [
    selectedServiceTypes,
    serviceTypes,
    serviceNameMap,
    serviceTypesInfo,
    defaultPharmacyServices,
  ]);

  const displayRooms = React.useMemo(() => {
    if (selectedRooms && selectedRooms.length > 0) {
      return selectedRooms.map((roomFilter) => {
        const matchingResource = (
          resources ||
          dynamicResources ||
          pharmacyRooms ||
          []
        ).find(
          (r: any) =>
            String(r.id || r._id) === String(roomFilter) ||
            String(r.name).toLowerCase() === String(roomFilter).toLowerCase(),
        );
        return matchingResource ? matchingResource.name : roomFilter;
      });
    }
    return roomNames;
  }, [selectedRooms, roomNames, resources, dynamicResources, pharmacyRooms]);

  // Compute columns based on column mode
  const weekViewColumns = React.useMemo(() => {
    const viewDays =
      view === "week"
        ? getWeekDays(currentDate)
        : view === "threeday"
          ? getThreeDays(currentDate)
          : view === "workweek"
            ? getWorkWeekDays(currentDate)
            : view === "day"
              ? [currentDate]
              : getWeekDays(currentDate);

    if (columnMode === "practitioner") {
      return displayPharmacists;
    } else if (columnMode === "day" || columnMode === "day-practitioner") {
      return viewDays.map((day) => formatDateFromDate(day));
    } else if (columnMode === "services") {
      return displayServices;
    } else if (columnMode === "room") {
      return displayRooms;
    }
    return displayPharmacists;
  }, [
    columnMode,
    displayPharmacists,
    displayServices,
    displayRooms,
    currentDate,
    view,
    serviceTypes,
    roomNames,
    getWeekDays,
    getThreeDays,
    getWorkWeekDays,
    formatDateFromDate,
  ]);

  const handleDurationChange = (bookingId: string, newDuration: number) => {
    // Find the booking across all dates
    const allDates = getDaysInMonth(currentDate)
      .filter((d) => d !== null)
      .map((d) =>
        formatDate(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          d as number,
        ),
      );

    for (const dateString of allDates) {
      const bookings = getFilteredBookingsForDate(dateString);
      const booking = bookings.find((b) => b.id === bookingId);
      if (booking) {
        const updatedBooking = {
          ...booking,
          duration: newDuration,
        };
        onEditBooking(bookingId, updatedBooking);
        return;
      }
    }
  };

  const handleBlockOutDurationChange = (
    blockOutId: string,
    newEndTime: string,
  ) => {
    // Find the block-out across all dates
    const allDates = getDaysInMonth(currentDate)
      .filter((d) => d !== null)
      .map((d) =>
        formatDate(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          d as number,
        ),
      );

    for (const dateString of allDates) {
      const blockOuts = getFilteredBlockOutsForDate(dateString);
      const blockOut = blockOuts.find((bo) => bo.id === blockOutId);
      if (blockOut) {
        const updatedBlockOut = {
          ...blockOut,
          endTime: newEndTime,
        };
        onEditBlockOut(blockOutId, updatedBlockOut);
        return;
      }
    }
  };

  // Helper function to get block-outs for a specific time slot (including half hours)
  const getBlockOutsForTimeSlot = (dateString: string, timeSlot: number) => {
    return getFilteredBlockOutsForTimeSlot(dateString, timeSlot);
  };

  const blockOutMatchesPractitioner = (bo: BlockOut, pharmacistName: string) => {
    if (!bo.affectedPharmacists || bo.affectedPharmacists.length === 0)
      return false;
    const normP = String(pharmacistName).trim().toLowerCase();
    const res = (resources || []).find(
      (r: any) => String(r.name).trim().toLowerCase() === normP,
    );
    const pId = res ? String(res.id).trim().toLowerCase() : normP;

    return bo.affectedPharmacists.some((p: any) => {
      const normAff = String(p).trim().toLowerCase();
      return normAff === normP || normAff === pId;
    });
  };

  const blockOutMatchesRoom = (bo: BlockOut, roomName: string) => {
    const hasAffectedRooms = bo.affectedRooms && bo.affectedRooms.length > 0;
    const hasAffectedPharmacists =
      bo.affectedPharmacists && bo.affectedPharmacists.length > 0;

    if (hasAffectedRooms) {
      return bo.affectedRooms!.some((r: any) => {
        const rName = (s as any)?.roomNameMap?.[String(r)] || r;
        const normCol = String(roomName).trim().toLowerCase();
        return (
          normCol === String(r).trim().toLowerCase() ||
          normCol === String(rName).trim().toLowerCase()
        );
      });
    }

    return !hasAffectedRooms && !hasAffectedPharmacists;
  };

  // Sync remote / filtered tasks strictly into state without local duplicates
  useEffect(() => {
    if ((s as any)?.filteredTasks) {
      setTasks((s as any).filteredTasks);
    } else if ((s as any)?.remoteTasks) {
      setTasks((s as any).remoteTasks);
    }
  }, [(s as any)?.filteredTasks, (s as any)?.remoteTasks]);

  // Helper function to get tasks for a specific time slot
  const getTasksForTimeSlot = (dateString: string, timeSlot: number) => {
    if (getFilteredTasksForTimeSlot) {
      return getFilteredTasksForTimeSlot(dateString, timeSlot);
    }
    const hour = Math.floor(timeSlot);
    const minutes = timeSlot % 1 === 0 ? 0 : 30;

    // Filter tasks that match the date and time
    return tasks.filter((task) => {
      if (task.dueDate !== dateString) return false;
      if (!task.dueTime) return true; // Show on date if no specific time set

      const [tHour, tMin] = task.dueTime.split(":").map(Number);
      return tHour === hour && (minutes === 0 ? tMin < 30 : tMin >= 30);
    });
  };

  // Handler for editing tasks
  const handleEditTask = (taskId: string, task?: Task) => {
    const taskToEdit = task || tasks.find((t) => t.id === taskId);
    if (taskToEdit) {
      if (
        taskToEdit.otherPharmacyId &&
        String(taskToEdit.otherPharmacyId) !== String(currentPharmacyId)
      ) {
        return; // View-only for tasks from other pharmacies
      }
      setEditingTask(taskToEdit);
      setShowTaskModal(true);
    }
  };

  // Handler for saving task (re-sync with API data only)
  const handleSaveTask = async (_taskData: any) => {
    try {
      if (Array.isArray(selectedPharmacies) && selectedPharmacies.length > 0) {
        await getScheduleEventsByPharmacies(selectedPharmacies);
      } else if (currentPharmacyId) {
        await refreshScheduleEventsSilently(currentPharmacyId);
      }
    } catch (err) {
      console.error("Failed to refresh schedule events silently:", err);
    }
    setShowTaskModal(false);
    setEditingTask(null);
  };

  // Narrower time gutter and columns on phones; the grid still scrolls
  // horizontally when the columns don't fit.
  const timeGridColumns = `${isMobile ? 56 : 120}px repeat(${weekViewColumns.length}, minmax(${isMobile ? 112 : 150}px, 1fr))`;

  if (isEventsLoading || isResourcesLoading) {
    return <ScheduleSkeleton isDarkMode={isDarkMode} />;
  }

  return (
    <div
      ref={rootRef}
      style={
        // On phones the app header above wraps onto several rows and keeps
        // reflowing after mount, so "viewport minus top" can come out near 0
        // and the overflow-hidden content area clips the whole calendar.
        // Give it a fixed share of the screen and let the page scroll instead.
        isMobile
          ? { height: "85dvh", minHeight: 480, flex: "none" }
          : availableHeight !== null
            ? { height: availableHeight, flex: "none" }
            : undefined
      }
      className={`flex-1 flex flex-col h-screen isolate transition-colors duration-300 ${
        isDarkMode ? "bg-gray-900" : "bg-white"
      }`}
    >
      {/* Left Sidebar removed - filters moved to header */}
      {false && (
        <div className="hidden">
          <div className="space-y-6">
            {/* Small Month Calendar */}
            <div>
              <div
                className={`border rounded-lg overflow-hidden transition-colors duration-300 ${
                  isDarkMode
                    ? "border-gray-600 bg-gray-700 [&_.rdp]:text-white [&_.rdp-button]:text-white [&_.rdp-button]:hover:bg-gray-600"
                    : "border-gray-200 bg-white"
                }`}
              >
                <Calendar
                  mode="single"
                  selected={calendarSelectedDate}
                  onSelect={(date) => onCalendarSelect(date || undefined)}
                  className="w-full scale-90 origin-top"
                  classNames={{
                    day_selected:
                      "bg-purple-600 text-white hover:bg-purple-700",
                    day_today: isDarkMode
                      ? "bg-blue-600 text-white hover:bg-blue-700"
                      : "bg-gray-100 text-gray-900",
                    ...(isDarkMode && {
                      months:
                        "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                      month: "space-y-4",
                      caption:
                        "flex justify-center pt-1 relative items-center text-white",
                      caption_label: "text-sm font-medium text-white",
                      nav_button:
                        "h-7 w-7 bg-transparent p-0 opacity-70 hover:opacity-100 text-white hover:bg-gray-700 border border-gray-600 rounded",
                      table: "w-full border-collapse space-y-1",
                      head_row: "flex",
                      head_cell:
                        "text-gray-400 rounded-md w-9 font-normal text-xs",
                      row: "flex w-full mt-2",
                      cell: "h-9 w-9 text-center text-sm p-0 relative",
                      day: "h-9 w-9 p-0 font-normal hover:bg-gray-700 text-white rounded",
                      day_outside: "text-gray-600 opacity-50",
                      day_disabled: "text-gray-600 opacity-50",
                    }),
                  }}
                  components={{
                    DayContent: ({ date }) => {
                      const dateString = formatDateFromDate(date);
                      const dayBookings = getBookingsForDate(dateString);
                      const hasBookings = dayBookings.length > 0;
                      const hasBlockOuts = dayBlockOuts.length > 0;

                      return (
                        <div className="relative w-full h-full flex items-center justify-center">
                          {date.getDate()}
                          <div className="absolute -top-2.5 left-1/2 transform -translate-x-1/2 flex gap-1">
                            {hasBookings && (
                              <div
                                className={`w-1.5 h-1.5 rounded-full transition-colors duration-300 ${
                                  isDarkMode ? "bg-orange-400" : "bg-orange-500"
                                }`}
                              />
                            )}
                            {hasBlockOuts && (
                              <div
                                className={`w-1.5 h-1.5 transition-colors duration-300 ${
                                  isDarkMode ? "bg-red-400" : "bg-red-500"
                                }`}
                                style={{
                                  clipPath: "polygon(0 0, 100% 0, 50% 100%)",
                                }}
                              />
                            )}
                          </div>
                        </div>
                      );
                    },
                  }}
                  onDayClick={(date) => onCalendarSelect(date)}
                />
              </div>
            </div>

            {/* Filters Header */}
            <div className="flex items-center gap-2">
              <FilterIcon
                className={`w-4 h-4 transition-colors duration-300 ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              />
              <h3
                className={`text-lg font-semibold transition-colors duration-300 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Filters
              </h3>
            </div>

            {/* Pharmacies Filter */}
            <PharmaciesFilter
              selectedPharmacies={selectedPharmacies}
              onPharmacyChange={handlePharmacyChange}
              onSelectAll={handleSelectAllPharmacies}
              isDarkMode={isDarkMode}
              pharmacies={pharmacies}
            />

            {/* Service Type Filters */}
            <ServiceTypeFilter
              selectedServiceTypes={selectedServiceTypes}
              onServiceTypeChange={handleServiceTypeChange}
              onSelectAll={handleSelectAllServices}
              isDarkMode={isDarkMode}
            />

            {/* Pharmacist Filters */}
            <PharmacistFilter
              selectedPharmacists={selectedPharmacists}
              onPharmacistChange={handlePharmacistChange}
              onSelectAll={handleSelectAllPharmacists}
              pharmacists={pharmacistNames}
              isDarkMode={isDarkMode}
            />

            {/* Room Filters */}
            <RoomFilter
              selectedRooms={selectedRooms}
              onRoomChange={handleRoomChange}
              onSelectAll={handleSelectAllRooms}
              roomNames={roomNames}
              isDarkMode={isDarkMode}
            />

            {/* Block-Out Types Filters */}
            <BlockOutTypesFilter
              selectedBlockOutTypes={selectedBlockOutTypes}
              onBlockOutTypeChange={handleBlockOutTypeChange}
              onSelectAll={handleSelectAllBlockOutTypes}
              isDarkMode={isDarkMode}
            />

            {/* Service Types Info */}
            <ServiceTypesInfo isDarkMode={isDarkMode} />
          </div>
        </div>
      )}

      {/* Main Content Area - Non-scrollable */}
      <div className="flex-1 flex flex-col max-h-screen overflow-hidden">
        {/* Calendar Header */}
        <div
          className={`border-b p-3 md:p-6 transition-colors duration-300 ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          {/* On phones: actions + view settings on row 1, date navigation on row 2 */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            {/* Left side - Action buttons & Filters */}
            <div className="flex items-center gap-2">
              {/* Filters Button */}
              <ScheduleFilters
                isDarkMode={isDarkMode}
                selectedServiceTypes={selectedServiceTypes}
                onServiceTypeChange={handleServiceTypeChange}
                onSelectAllServices={handleSelectAllServices}
                serviceTypes={serviceTypes}
                selectedPharmacists={selectedPharmacists}
                onPharmacistChange={handlePharmacistChange}
                onSelectAllPharmacists={handleSelectAllPharmacists}
                pharmacists={pharmacistNames}
                selectedRooms={selectedRooms}
                onRoomChange={handleRoomChange}
                onSelectAllRooms={handleSelectAllRooms}
                roomNames={roomNames}
                selectedBlockOutTypes={selectedBlockOutTypes}
                onBlockOutTypeChange={handleBlockOutTypeChange}
                onSelectAllBlockOutTypes={handleSelectAllBlockOutTypes}
                selectedPharmacies={selectedPharmacies}
                onPharmacyChange={handlePharmacyChange}
                onSelectAllPharmacies={handleSelectAllPharmacies}
                pharmacies={pharmacies}
              />
              {/* Add Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm"
                    className="bg-slate-900 hover:bg-slate-800 gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className={`${
                    isDarkMode
                      ? "bg-gray-800 border-gray-700"
                      : "bg-white border-gray-200"
                  }`}
                >
                  <DropdownMenuItem
                    onClick={openNewAppointment}
                    className={`cursor-pointer ${
                      isDarkMode
                        ? "hover:bg-gray-700 focus:bg-gray-700"
                        : "hover:bg-gray-100 focus:bg-gray-100"
                    }`}
                  >
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    <span>New Appointment</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setShowBlockOutModal(true)}
                    className={`cursor-pointer ${
                      isDarkMode
                        ? "hover:bg-gray-700 focus:bg-gray-700"
                        : "hover:bg-gray-100 focus:bg-gray-100"
                    }`}
                  >
                    <Clock className="w-4 h-4 mr-2" />
                    <span>Block-Out Period</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      setEditingTask(null);
                      setShowTaskModal(true);
                    }}
                    className={`cursor-pointer ${
                      isDarkMode
                        ? "hover:bg-gray-700 focus:bg-gray-700"
                        : "hover:bg-gray-100 focus:bg-gray-100"
                    }`}
                  >
                    <CheckSquare className="w-4 h-4 mr-2" />
                    <span>Task</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Center - Month navigation */}
            <div className="order-last w-full md:order-none md:w-auto flex items-center justify-between md:justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-9 w-9 md:w-auto p-0 md:px-3"
                onClick={() => navigate("prev")}
                aria-label="Previous"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    className={`text-base md:text-lg font-medium min-w-0 md:min-w-[200px] truncate text-center transition-colors duration-300 hover:opacity-75 cursor-pointer ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {getViewTitle()}
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  className={`w-auto p-0 ${
                    isDarkMode
                      ? "bg-gray-800 border-gray-700"
                      : "bg-white border-gray-200"
                  }`}
                >
                  <Calendar
                    mode="single"
                    selected={calendarSelectedDate}
                    onSelect={(date) => {
                      if (date) {
                        onCalendarSelect(date);
                      }
                    }}
                    classNames={{
                      day_selected:
                        "bg-purple-600 text-white hover:bg-purple-700",
                      day_today: isDarkMode
                        ? "bg-blue-600 text-white hover:bg-blue-700"
                        : "bg-gray-100 text-gray-900",
                      ...(isDarkMode && {
                        months:
                          "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                        month: "space-y-4",
                        caption:
                          "flex justify-center pt-1 relative items-center text-white",
                        caption_label: "text-sm font-medium text-white",
                        nav_button:
                          "h-7 w-7 bg-transparent p-0 opacity-70 hover:opacity-100 text-white hover:bg-gray-700 border border-gray-600 rounded",
                        table: "w-full border-collapse space-y-1",
                        head_row: "flex",
                        head_cell:
                          "text-gray-400 rounded-md w-9 font-normal text-xs",
                        row: "flex w-full mt-2",
                        cell: "h-9 w-9 text-center text-sm p-0 relative",
                        day: "h-9 w-9 p-0 font-normal hover:bg-gray-700 text-white rounded",
                        day_outside: "text-gray-600 opacity-50",
                        day_disabled: "text-gray-600 opacity-50",
                      }),
                    }}
                  />
                </PopoverContent>
              </Popover>
              <Button
                variant="outline"
                size="sm"
                className="h-9 w-9 md:w-auto p-0 md:px-3"
                onClick={() => navigate("next")}
                aria-label="Next"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {/* Right side - View toggle */}
            <div className="flex items-center gap-2 md:gap-3">
              {/* Calendar Sync Button */}
              {ifCalendarIntegrationEnabled && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSyncModal(true)}
                  title={
                    googleCalendarStatus?.isConnected
                      ? `Connected with ${
                          googleCalendarStatus?.email || "Google Calendar"
                        }`
                      : "Sync with external calendars"
                  }
                  className="gap-2"
                >
                  <RefreshCw
                    className={`w-4 h-4 ${
                      isGoogleCalendarSyncing ? "animate-spin" : ""
                    }`}
                  />

                  <span className="hidden sm:inline">Sync Calendar</span>

                  {googleCalendarStatus?.isConnected && (
                    <span className="ml-1 flex items-center gap-1">
                      <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>

                      <span className="hidden sm:inline text-xs text-green-600 dark:text-green-400 font-medium">
                        Connected
                      </span>
                    </span>
                  )}
                </Button>
              )}

              {/* View Settings - combines view type, group by, and display mode */}
              <ViewSettings
                isDarkMode={isDarkMode}
                view={view}
                onViewChange={setView}
                columnMode={columnMode}
                onColumnModeChange={setColumnMode}
                displayMode={displayMode}
                onDisplayModeChange={setDisplayMode}
                showColumnMode={
                  (view === "day" ||
                    view === "week" ||
                    view === "threeday" ||
                    view === "workweek") &&
                  displayMode === "calendar"
                }
              />
            </div>
          </div>
        </div>

        {/* Calendar Views */}
        <div
          className={`relative flex-1 p-2 md:p-6 flex flex-col min-h-0 transition-colors duration-300 ${
            view === "month" || displayMode === "list"
              ? "overflow-y-auto visible-scrollbar"
              : "overflow-hidden"
          } ${
            isDarkMode ? "bg-gray-900" : ""
          }`}
        >
          {view === "month" && displayMode === "calendar" && (
            <>
              {/* Day Headers */}
              <div className="grid grid-cols-7 gap-px mb-px">
                {dayNames.map((day) => (
                  <div
                    key={day}
                    className={`p-1.5 md:p-3 text-center text-xs md:text-sm font-medium transition-colors duration-300 ${
                      isDarkMode
                        ? "bg-gray-700 text-gray-300"
                        : "bg-gray-50 text-gray-700"
                    }`}
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Days */}
              <div
                className={`grid grid-cols-7 gap-px transition-colors duration-300 ${
                  isDarkMode ? "bg-gray-600" : "bg-gray-200"
                }`}
              >
                {getDaysInMonth(currentDate).map((day, index) => {
                  if (day === null) {
                    return (
                      <div
                        key={`empty-${index}`}
                        className={`h-16 md:h-36 p-2 transition-colors duration-300 ${
                          isDarkMode ? "bg-gray-800" : "bg-gray-50"
                        }`}
                      />
                    );
                  }

                  const dateString = formatDate(
                    currentDate.getFullYear(),
                    currentDate.getMonth(),
                    day,
                  );
                  const dayBookings = getBookingsForDate(dateString);
                  const dayBlockOuts = getFilteredBlockOutsForDate(dateString);
                  const dayTasks = getFilteredTasksForDate
                    ? getFilteredTasksForDate(dateString)
                    : tasks.filter((task) => task.dueDate === dateString);
                  const isToday =
                    new Date().toDateString() ===
                    new Date(dateString).toDateString();

                  const dayDate = new Date(`${dateString}T00:00:00`);
                  const isDayWorking = checkSlotWorkingStatus(
                    dayDate,
                    undefined,
                    "day",
                    dateString,
                    selectedPharmacists,
                    selectedRooms,
                    selectedServiceTypes,
                    resources,
                    pharmacistNames,
                    roomNames,
                  );

                  return (
                    <CalendarDayCell
                      key={day}
                      day={day}
                      dateString={dateString}
                      dayBookings={dayBookings}
                      dayBlockOuts={dayBlockOuts}
                      dayTasks={dayTasks}
                      isToday={isToday}
                      isNonWorking={!isDayWorking}
                      serviceColors={serviceColors}
                      serviceNameMap={serviceNameMap}
                      serviceColorMap={serviceColorMap}
                      dayGoogleEvents={getDayGoogleEvents(dateString)}
                      onCreateBooking={onCreateBooking}
                      onEditBooking={onShowEditBooking}
                      onDeleteBooking={onDeleteBooking}
                      onStartConsultation={handleStartConsultation}
                      onEditBlockOut={onShowEditBlockOut}
                      onEditTask={handleEditTask}
                      onViewDay={handleViewDay}
                      onCreateBookingForDate={onCreateBookingForDate}
                      pharmacists={pharmacistNames}
                      rooms={roomNames}
                      isDarkMode={isDarkMode}
                      onBookingDrop={handleBookingDrop}
                      notifiedBookingId={notifiedBookingId}
                      notificationType={notificationType}
                      highlightedBookingId={highlightedAppointmentId}
                    />
                  );
                })}
              </div>
            </>
          )}

          {view === "month" && displayMode === "list" && (
            <div
              className={`shrink-0 rounded-lg border overflow-hidden transition-colors duration-300 ${
                isDarkMode
                  ? "bg-gray-800 border-gray-600"
                  : "bg-white border-gray-200"
              }`}
            >
              <div className={`overflow-x-auto`}>
                <table className="w-full">
                  <thead>
                    <tr
                      className={`border-b transition-colors duration-300 ${
                        isDarkMode
                          ? "bg-gray-700 border-gray-600"
                          : "bg-gray-50 border-gray-200"
                      }`}
                    >
                      <th
                        className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Date
                      </th>
                      <th
                        className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Time
                      </th>
                      <th
                        className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 w-48 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Patient
                      </th>
                      <th
                        className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Service Type
                      </th>
                      <th
                        className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Pharmacist
                      </th>
                      <th
                        className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Actions
                      </th>
                      <th
                        className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Vaccines Required
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {getDaysInMonth(currentDate).map((day, index) => {
                      if (day === null) return null;

                      const dateString = formatDate(
                        currentDate.getFullYear(),
                        currentDate.getMonth(),
                        day,
                      );
                      const dayBookings = getBookingsForDate(dateString);

                      return dayBookings.map((booking) => {
                        const matchedService = defaultPharmacyServices?.find(
                          (service) => service._id === booking.serviceId,
                        );

                        const serviceName = matchedService?.serviceName || "";
                        const serviceColor =
                          matchedService?.serviceColor ||
                          booking.serviceColor ||
                          "";

                        const bookingDate = new Date(dateString);
                        const dayOfWeek = [
                          "Sun",
                          "Mon",
                          "Tue",
                          "Wed",
                          "Thu",
                          "Fri",
                          "Sat",
                        ][bookingDate.getDay()];
                        const month = String(
                          bookingDate.getMonth() + 1,
                        ).padStart(2, "0");
                        const dayNum = String(bookingDate.getDate()).padStart(
                          2,
                          "0",
                        );
                        const formattedDate = `${dayNum}/${month} ${dayOfWeek}`;

                        const [startHour, startMin] = booking.time
                          .split(":")
                          .map(Number);
                        const endMinutes =
                          startHour * 60 + startMin + booking.duration;
                        const endHour = Math.floor(endMinutes / 60);
                        const endMin = endMinutes % 60;

                        const formatTime = (hour: number, min: number) => {
                          const period = hour >= 12 ? "pm" : "am";
                          const displayHour =
                            hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                          return min === 0
                            ? `${displayHour}${period}`
                            : `${displayHour}:${String(min).padStart(2, "0")}${period}`;
                        };

                        const timeRange = `${formatTime(startHour, startMin)}-${formatTime(endHour, endMin)}`;

                        return (
                          <tr
                            key={`${dateString}-${booking.id}`}
                            className={`border-b transition-colors duration-300 hover:${
                              isDarkMode ? "bg-gray-700" : "bg-gray-50"
                            } cursor-pointer ${
                              isDarkMode ? "border-gray-600" : "border-gray-200"
                            }`}
                            onClick={() =>
                              onShowEditBooking(booking.id, booking)
                            }
                          >
                            <td
                              className={`px-6 py-4 text-sm transition-colors duration-300 whitespace-nowrap font-medium ${
                                isDarkMode ? "text-gray-300" : "text-gray-900"
                              }`}
                            >
                              {formattedDate}
                            </td>
                            <td
                              className={`px-6 py-4 text-sm transition-colors duration-300 whitespace-nowrap ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              {timeRange}
                            </td>
                            <td
                              className={`px-6 py-4 text-sm transition-colors duration-300 max-w-xs ${
                                isDarkMode ? "text-gray-300" : "text-gray-900"
                              }`}
                            >
                              <div className="truncate font-medium">
                                {booking.patientName}
                              </div>
                            </td>
                            <td
                              className={`px-6 py-4 text-sm transition-colors duration-300 ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              {serviceName?.trim() ? (
                                <span
                                  className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium"
                                  style={{
                                    backgroundColor: `${serviceColor}20`,
                                    color: serviceColor,
                                  }}
                                >
                                  <span
                                    className="w-2 h-2 rounded-full"
                                    style={{ backgroundColor: serviceColor }}
                                  />
                                  {serviceName}
                                </span>
                              ) : (
                                <span
                                  className={`${
                                    isDarkMode
                                      ? "text-gray-500"
                                      : "text-gray-400"
                                  }`}
                                >
                                  None
                                </span>
                              )}
                            </td>
                            <td
                              className={`px-6 py-4 text-sm transition-colors duration-300 ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              {booking.pharmacist}
                            </td>
                            <td
                              className={`px-6 py-4 text-sm transition-colors duration-300 ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              {booking.tasksToComplete &&
                              booking.tasksToComplete.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {booking.tasksToComplete.map(
                                    (task: any, idx: number) => {
                                      const label =
                                        task?.label ||
                                        task?.text ||
                                        task?.name ||
                                        (typeof task === "string"
                                          ? task
                                          : String(task));
                                      return (
                                        <span
                                          key={task?.id || idx}
                                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                                            isDarkMode
                                              ? "bg-blue-900/30 text-blue-300"
                                              : "bg-blue-100 text-blue-800"
                                          }`}
                                          title={label}
                                        >
                                          {label}
                                        </span>
                                      );
                                    },
                                  )}
                                </div>
                              ) : (
                                <span
                                  className={`${
                                    isDarkMode
                                      ? "text-gray-500"
                                      : "text-gray-400"
                                  }`}
                                >
                                  None
                                </span>
                              )}
                            </td>
                            <td
                              className={`px-6 py-4 text-sm transition-colors duration-300 ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              {(booking.vaccinesToAdminister ||
                                booking.vaccinesAdminister) &&
                              ((booking.vaccinesToAdminister &&
                                booking.vaccinesToAdminister.length > 0) ||
                                (booking.vaccinesAdminister &&
                                  booking.vaccinesAdminister.length > 0)) ? (
                                <div className="flex flex-wrap gap-1">
                                  {(
                                    booking.vaccinesToAdminister ||
                                    booking.vaccinesAdminister ||
                                    []
                                  ).map((vaccine: any, idx: number) => {
                                    const name =
                                      vaccine?.vaccineName ||
                                      vaccine?.VaccineName ||
                                      vaccine?.name ||
                                      vaccine?.Name ||
                                      (typeof vaccine === "string"
                                        ? vaccine
                                        : String(vaccine));
                                    return (
                                      <span
                                        key={vaccine?.id || vaccine?.Id || idx}
                                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                                          isDarkMode
                                            ? "bg-amber-900/30 text-amber-300"
                                            : "bg-amber-100 text-amber-800"
                                        }`}
                                        title={
                                          vaccine?.dose || vaccine?.Manufacturer
                                            ? `${vaccine.dose || vaccine.Manufacturer}`
                                            : ""
                                        }
                                      >
                                        {name}
                                      </span>
                                    );
                                  })}
                                </div>
                              ) : (
                                <span
                                  className={`${
                                    isDarkMode
                                      ? "text-gray-500"
                                      : "text-gray-400"
                                  }`}
                                >
                                  None
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      });
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {(view === "week" ||
            view === "threeday" ||
            view === "workweek" ||
            view === "day") &&
            displayMode === "calendar" && (
              <div
                ref={scrollContainerRef}
                data-calendar-scroll-container="true"
                className={`flex-1 min-h-0 border rounded-lg overflow-auto transition-colors duration-300 relative ${
                  isDarkMode
                    ? "bg-gray-800 border-gray-600"
                    : "bg-white border-gray-200"
                }`}
              >
                {/* Sticky Header: Column Headers & Sub-Headers */}
                <div
                  data-calendar-header="true"
                  className={`sticky top-0 z-40 min-w-full w-fit rounded-t-lg transition-colors duration-300 ${
                    isDarkMode ? "bg-gray-800" : "bg-white"
                  }`}
                >
                  {/* Column Headers */}
                  <div
                    className={`grid border-b transition-colors duration-300 ${
                      isDarkMode ? "border-gray-600" : "border-gray-200"
                    }`}
                    style={{
                      gridTemplateColumns: timeGridColumns,
                    }}
                  >
                    <div
                      className={`p-2 md:p-3 border-r transition-colors duration-300 sticky left-0 z-30 rounded-tl-lg ${
                        isDarkMode
                          ? "bg-gray-700 border-gray-600"
                          : "bg-gray-50 border-gray-200"
                      }`}
                    >
                      <div
                        className={`text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Time
                      </div>
                    </div>
                    {weekViewColumns.map((column, index) => {
                      if (columnMode === "practitioner") {
                        const pharmacistName = column;
                        return (
                          <div
                            key={index}
                            className={`p-3 text-center border-r transition-colors duration-300 ${
                              isDarkMode
                                ? "bg-gray-700 border-gray-600"
                                : "bg-gray-50 border-gray-200"
                            }`}
                          >
                            <div
                              className={`text-sm font-medium transition-colors duration-300 ${
                                isDarkMode ? "text-white" : "text-gray-900"
                              }`}
                            >
                              {pharmacistName}
                            </div>
                          </div>
                        );
                      } else if (
                        columnMode === "day" ||
                        columnMode === "day-practitioner"
                      ) {
                        const dateString = column;
                        const dayShort = getDayShortName(dateString);
                        const date = new Date(dateString);
                        const isToday =
                          new Date().toDateString() === date.toDateString();
                        return (
                          <div
                            key={index}
                            className={`p-3 text-center border-r transition-colors duration-300 ${
                              isToday
                                ? isDarkMode
                                  ? "bg-blue-900/30 border-gray-600"
                                  : "bg-blue-50 border-gray-200"
                                : isDarkMode
                                  ? "bg-gray-700 border-gray-600"
                                  : "bg-gray-50 border-gray-200"
                            }`}
                          >
                            <div
                              className={`text-sm font-medium transition-colors duration-300 ${
                                isToday
                                  ? isDarkMode
                                    ? "text-blue-300"
                                    : "text-blue-600"
                                  : isDarkMode
                                    ? "text-white"
                                    : "text-gray-900"
                              }`}
                            >
                              {dayShort}
                            </div>
                            <div
                              className={`text-xs transition-colors duration-300 ${
                                isDarkMode ? "text-gray-400" : "text-gray-500"
                              }`}
                            >
                              {date.getDate()}
                            </div>
                          </div>
                        );
                      } else if (columnMode === "services") {
                        const serviceType = column;
                        return (
                          <div
                            key={index}
                            className={`p-3 text-center border-r transition-colors duration-300 ${
                              isDarkMode
                                ? "bg-gray-700 border-gray-600"
                                : "bg-gray-50 border-gray-200"
                            }`}
                          >
                            <div
                              className={`text-sm font-medium transition-colors duration-300 ${
                                isDarkMode ? "text-white" : "text-gray-900"
                              }`}
                            >
                              {serviceType}
                            </div>
                          </div>
                        );
                      } else if (columnMode === "room") {
                        const roomName = column;
                        return (
                          <div
                            key={index}
                            className={`p-3 text-center border-r transition-colors duration-300 ${
                              isDarkMode
                                ? "bg-gray-700 border-gray-600"
                                : "bg-gray-50 border-gray-200"
                            }`}
                          >
                            <div
                              className={`text-sm font-medium transition-colors duration-300 ${
                                isDarkMode ? "text-white" : "text-gray-900"
                              }`}
                            >
                              {roomName}
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })}
                  </div>

                  {/* Sub-Headers Row - Only show for practitioner, services, and room modes */}
                  {(columnMode === "practitioner" ||
                    columnMode === "services" ||
                    columnMode === "room") && (
                    <div
                      className={`grid border-b transition-colors duration-300 ${
                        isDarkMode
                          ? "border-gray-600 bg-gray-800"
                          : "border-gray-200 bg-gray-100"
                      }`}
                      style={{
                        gridTemplateColumns: timeGridColumns,
                      }}
                    >
                      <div
                        className={`p-2 border-r transition-colors duration-300 sticky left-0 z-30 ${
                          isDarkMode ? "border-gray-600 bg-gray-800" : "border-gray-200 bg-gray-100"
                        }`}
                      />
                      {weekViewColumns.map((column, colIndex) => {
                        const viewDays =
                          view === "week"
                            ? getWeekDays(currentDate)
                            : view === "threeday"
                              ? getThreeDays(currentDate)
                              : view === "workweek"
                                ? getWorkWeekDays(currentDate)
                                : view === "day"
                                  ? [currentDate]
                                  : getWeekDays(currentDate);
                        const numDays = viewDays.length;
                        return (
                          <div
                            key={colIndex}
                            className={`grid border-r transition-colors duration-300 ${
                              isDarkMode ? "border-gray-600" : "border-gray-200"
                            }`}
                            style={{
                              gridTemplateColumns: `repeat(${numDays}, 1fr)`,
                            }}
                          >
                            {viewDays.map((day, dayIndex) => {
                              const dateString = formatDateFromDate(day);
                              const dayShort = getDayShortName(dateString);
                              const isToday =
                                new Date().toDateString() === day.toDateString();

                              return (
                                <div
                                  key={dayIndex}
                                  className={`p-1 text-center text-xs font-medium border-r last:border-r-0 transition-colors duration-300 ${
                                    isToday
                                      ? isDarkMode
                                        ? "bg-blue-900/30 text-blue-300"
                                        : "bg-blue-50 text-blue-600"
                                      : isDarkMode
                                        ? "text-gray-400"
                                        : "text-gray-600"
                                  } ${
                                    isDarkMode
                                      ? "border-gray-700"
                                      : "border-gray-300"
                                  }`}
                                >
                                  <div>{dayShort}</div>
                                  <div
                                    className={`text-[10px] ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}
                                  >
                                    {day.getDate()}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Sub-Headers Row - Practitioners nested under each day, for day-practitioner mode */}
                  {columnMode === "day-practitioner" && (
                    <div
                      className={`grid border-b transition-colors duration-300 ${
                        isDarkMode
                          ? "border-gray-600 bg-gray-800"
                          : "border-gray-200 bg-gray-100"
                      }`}
                      style={{
                        gridTemplateColumns: timeGridColumns,
                      }}
                    >
                      <div
                        className={`p-2 border-r transition-colors duration-300 sticky left-0 z-30 ${
                          isDarkMode ? "border-gray-600 bg-gray-800" : "border-gray-200 bg-gray-100"
                        }`}
                      />
                      {weekViewColumns?.map((column, colIndex) => (
                        <div
                          key={colIndex}
                          className={`grid border-r transition-colors duration-300 ${
                            isDarkMode ? "border-gray-600" : "border-gray-200"
                          }`}
                          style={{
                            gridTemplateColumns: `repeat(${displayPharmacists.length}, 1fr)`,
                          }}
                        >
                          {displayPharmacists?.map((pharmacistName, pIndex) => (
                            <div
                              key={pIndex}
                              className={`p-1 text-center text-xs font-medium border-r last:border-r-0 truncate transition-colors duration-300 ${
                                isDarkMode
                                  ? "text-gray-400 border-gray-700"
                                  : "text-gray-600 border-gray-300"
                              }`}
                              title={pharmacistName}
                            >
                              {getPractitionerInitials(pharmacistName)}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Time Slots with Pharmacist Columns */}
                {weekTimeSlots.map((timeSlot) => {
                  const timeLabel = formatTimeSlotLabel(timeSlot);

                  return (
                    <div
                      key={timeSlot}
                      data-timeslot={timeSlot}
                      className={`grid border-b transition-colors duration-300 relative overflow-visible min-w-full w-fit ${
                        isDarkMode ? "border-gray-600" : "border-gray-200"
                      }`}
                      style={{
                        gridTemplateColumns: timeGridColumns,
                      }}
                    >
                      <div
                        className={`p-2 border-r transition-colors duration-300 sticky left-0 z-10 ${
                          isDarkMode
                            ? "bg-gray-700 border-gray-600"
                            : "bg-gray-50 border-gray-200"
                        }`}
                      >
                        <div
                          className={`text-xs transition-colors duration-300 ${
                            isDarkMode ? "text-gray-300" : "text-gray-600"
                          }`}
                        >
                          {timeLabel}
                        </div>
                      </div>

                      {weekViewColumns.map((column, columnIndex) => {
                        const viewDays =
                          view === "week"
                            ? getWeekDays(currentDate)
                            : view === "threeday"
                              ? getThreeDays(currentDate)
                              : view === "workweek"
                                ? getWorkWeekDays(currentDate)
                                : view === "day"
                                  ? [currentDate]
                                  : getWeekDays(currentDate);

                        const weekBookingsByDay: Record<string, Booking[]> = {};
                        const weekBlockOutsByDay: Record<string, any[]> = {};
                        const weekTasksByDay: Record<string, Task[]> = {};
                        const weekGoogleEventsByDay: Record<
                          string,
                          GoogleCalendarEvent[]
                        > = {};
                        const nonWorkingHoursByDay: Record<string, boolean> =
                          {};

                        const timeString = timeLabel;

                        if (columnMode === "practitioner") {
                          // Practitioner mode: Group bookings by day for this practitioner
                          const pharmacistName = column;

                          viewDays.forEach((day) => {
                            const dateString = formatDateFromDate(day);
                            const slotBookings = getBookingsForTimeSlot(
                              dateString,
                              timeSlot,
                            );
                            const pharmacistBookings = slotBookings.filter(
                              (b) => b.pharmacist === pharmacistName,
                            );
                            weekBookingsByDay[dateString] = pharmacistBookings;

                            const slotBlockOuts = getBlockOutsForTimeSlot(
                              dateString,
                              timeSlot,
                            );
                            const pharmacistBlockOuts = slotBlockOuts.filter(
                              (bo) =>
                                blockOutMatchesPractitioner(bo, pharmacistName),
                            );
                            weekBlockOutsByDay[dateString] =
                              pharmacistBlockOuts;

                            const slotTasks = getTasksForTimeSlot(
                              dateString,
                              timeSlot,
                            );
                            const pharmacistTasks = slotTasks.filter((t) => {
                              const normP = (pharmacistName || "")
                                .trim()
                                .toLowerCase();
                              const normAssigned = (t.assignedTo || "")
                                .trim()
                                .toLowerCase();
                              const pResource = resources.find(
                                (r: any) =>
                                  r.name &&
                                  r.name.trim().toLowerCase() === normP,
                              );
                              const pResId = (pResource?.id || "")
                                .trim()
                                .toLowerCase();

                              return (
                                normAssigned === normP ||
                                (pResId && normAssigned === pResId) ||
                                !t.assignedTo ||
                                t.assignedTo === ""
                              );
                            });
                            weekTasksByDay[dateString] = pharmacistTasks;

                            // Add Google Calendar events for this time slot, scoped to this practitioner's resourceId
                            const slotGoogleEvents = getGoogleEventsForTimeSlot(
                              dateString,
                              timeSlot,
                            ).filter((event) =>
                              googleEventBelongsToPharmacist(
                                event,
                                pharmacistName,
                                resources,
                              ),
                            );
                            weekGoogleEventsByDay[dateString] =
                              slotGoogleEvents;

                            const isWorking = checkSlotWorkingStatus(
                              day,
                              timeString,
                              columnMode,
                              column,
                              selectedPharmacists,
                              selectedRooms,
                              selectedServiceTypes,
                              resources,
                              pharmacistNames,
                              roomNames,
                            );
                            nonWorkingHoursByDay[dateString] = !isWorking;
                          });
                        } else if (columnMode === "day") {
                          // Day mode: Show all practitioners' bookings for this day
                          const dateString = column;
                          const slotBookings = getBookingsForTimeSlot(
                            dateString,
                            timeSlot,
                          );
                          weekBookingsByDay[dateString] = slotBookings;

                          const slotBlockOuts = getBlockOutsForTimeSlot(
                            dateString,
                            timeSlot,
                          );
                          weekBlockOutsByDay[dateString] = slotBlockOuts;

                          const slotTasks = getTasksForTimeSlot(
                            dateString,
                            timeSlot,
                          );
                          weekTasksByDay[dateString] = slotTasks;

                          // Add Google Calendar events for this time slot
                          const slotGoogleEvents = getGoogleEventsForTimeSlot(
                            dateString,
                            timeSlot,
                          );
                          weekGoogleEventsByDay[dateString] = slotGoogleEvents;

                          const dayObj = new Date(`${dateString}T00:00:00`);
                          const isWorking = checkSlotWorkingStatus(
                            dayObj,
                            timeString,
                            columnMode,
                            column,
                            selectedPharmacists,
                            selectedRooms,
                            selectedServiceTypes,
                            resources,
                            pharmacistNames,
                            roomNames,
                          );
                          nonWorkingHoursByDay[dateString] = !isWorking;
                        } else if (columnMode === "services") {
                          // Services mode: Group bookings by day for this service type
                          const serviceType = column;

                          viewDays.forEach((day) => {
                            const dateString = formatDateFromDate(day);
                            const slotBookings = getBookingsForTimeSlot(
                              dateString,
                              timeSlot,
                            );
                            const serviceBookings = slotBookings.filter((b) => {
                              const bName = b.serviceId
                                ? serviceNameMap[b.serviceId]
                                : b.serviceType;
                              return (
                                b.serviceType === serviceType ||
                                b.serviceId === serviceType ||
                                (bName &&
                                  bName.trim().toLowerCase() ===
                                    serviceType.trim().toLowerCase())
                              );
                            });
                            weekBookingsByDay[dateString] = serviceBookings;

                            const slotBlockOuts = getBlockOutsForTimeSlot(
                              dateString,
                              timeSlot,
                            );
                            weekBlockOutsByDay[dateString] = slotBlockOuts;

                            const slotTasks = getTasksForTimeSlot(
                              dateString,
                              timeSlot,
                            );
                            weekTasksByDay[dateString] = slotTasks;

                            // Add Google Calendar events for this time slot
                            const slotGoogleEvents = getGoogleEventsForTimeSlot(
                              dateString,
                              timeSlot,
                            );
                            weekGoogleEventsByDay[dateString] =
                              slotGoogleEvents;

                            const isWorking = checkSlotWorkingStatus(
                              day,
                              timeString,
                              columnMode,
                              column,
                              selectedPharmacists,
                              selectedRooms,
                              selectedServiceTypes,
                              resources,
                              pharmacistNames,
                              roomNames,
                            );
                            nonWorkingHoursByDay[dateString] = !isWorking;
                          });
                        } else if (columnMode === "room") {
                          // Room mode: Group bookings by day for this room
                          const roomName = column;

                          viewDays.forEach((day) => {
                            const dateString = formatDateFromDate(day);
                            const slotBookings = getBookingsForTimeSlot(
                              dateString,
                              timeSlot,
                            );
                            const roomBookings = slotBookings.filter((b) => {
                              const bRoom =
                                b.room ||
                                (b as any).roomId ||
                                (b as any).RoomId;
                              const bRoomId =
                                (b as any).roomId ||
                                (b as any).RoomId ||
                                b.room;
                              const resolvedRoomName =
                                (s as any)?.roomNameMap?.[String(bRoomId)] ||
                                (s as any)?.roomNameMap?.[String(bRoom)] ||
                                bRoom;

                              const normCol = String(roomName)
                                .trim()
                                .toLowerCase();
                              return (
                                normCol ===
                                  String(bRoom).trim().toLowerCase() ||
                                normCol ===
                                  String(bRoomId).trim().toLowerCase() ||
                                normCol ===
                                  String(resolvedRoomName).trim().toLowerCase()
                              );
                            });
                            weekBookingsByDay[dateString] = roomBookings;

                            const slotBlockOuts = getBlockOutsForTimeSlot(
                              dateString,
                              timeSlot,
                            );
                            const roomBlockOuts = slotBlockOuts.filter((bo) =>
                              blockOutMatchesRoom(bo, roomName),
                            );
                            weekBlockOutsByDay[dateString] = roomBlockOuts;

                            const slotTasks = getTasksForTimeSlot(
                              dateString,
                              timeSlot,
                            );
                            const roomTasks = slotTasks.filter((t: any) => {
                              const tRoom =
                                t.room || t.roomId || t.affectedRooms;
                              if (!tRoom) return false;
                              const roomsArray = Array.isArray(tRoom)
                                ? tRoom
                                : [tRoom];
                              return roomsArray.some((r: any) => {
                                const rName =
                                  (s as any)?.roomNameMap?.[String(r)] || r;
                                const normCol = String(roomName)
                                  .trim()
                                  .toLowerCase();
                                return (
                                  normCol === String(r).trim().toLowerCase() ||
                                  normCol === String(rName).trim().toLowerCase()
                                );
                              });
                            });
                            weekTasksByDay[dateString] = roomTasks;

                            // Add Google Calendar events for this time slot
                            const slotGoogleEvents = getGoogleEventsForTimeSlot(
                              dateString,
                              timeSlot,
                            );
                            weekGoogleEventsByDay[dateString] =
                              slotGoogleEvents;

                            const isWorking = checkSlotWorkingStatus(
                              day,
                              timeString,
                              columnMode,
                              column,
                              selectedPharmacists,
                              selectedRooms,
                              selectedServiceTypes,
                              resources,
                              pharmacistNames,
                              roomNames,
                            );
                            nonWorkingHoursByDay[dateString] = !isWorking;
                          });
                        } else if (columnMode === "day-practitioner") {
                          // Day mode: Show all practitioners' bookings for this day
                          const dateString = column;
                          const slotBookings = getBookingsForTimeSlot(
                            dateString,
                            timeSlot,
                          );
                          weekBookingsByDay[dateString] = slotBookings;

                          const slotBlockOuts = getBlockOutsForTimeSlot(
                            dateString,
                            timeSlot,
                          );
                          weekBlockOutsByDay[dateString] = slotBlockOuts;

                          const slotTasks = getTasksForTimeSlot(
                            dateString,
                            timeSlot,
                          );
                          weekTasksByDay[dateString] = slotTasks;

                          // Add Google Calendar events for this time slot
                          const slotGoogleEvents = getGoogleEventsForTimeSlot(
                            dateString,
                            timeSlot,
                          );
                          weekGoogleEventsByDay[dateString] = slotGoogleEvents;

                          const dayObj = new Date(`${dateString}T00:00:00`);
                          const isWorking = checkSlotWorkingStatus(
                            dayObj,
                            timeString,
                            columnMode,
                            column,
                            selectedPharmacists,
                            selectedRooms,
                            selectedServiceTypes,
                            resources,
                            pharmacistNames,
                            roomNames,
                          );
                          nonWorkingHoursByDay[dateString] = !isWorking;
                        }

                        // Lanes so overlapping block-outs sit side by side instead of stacking
                        const blockOutLanesByDay: Record<
                          string,
                          Record<string, BlockOutLane>
                        > = {};
                        Object.entries(weekBlockOutsByDay).forEach(
                          ([dateString, slotBlockOuts]) => {
                            if (!slotBlockOuts?.length) return;
                            const dayBlockOuts = (
                              getFilteredBlockOutsForDate(dateString) as BlockOut[]
                            ).filter((bo) =>
                              columnMode === "practitioner"
                                ? blockOutMatchesPractitioner(bo, column)
                                : columnMode === "room"
                                  ? blockOutMatchesRoom(bo, column)
                                  : true,
                            );
                            blockOutLanesByDay[dateString] = getBlockOutLanes(
                              dayBlockOuts,
                              dateString,
                            );
                          },
                        );

                        const firstDayDateString =
                          columnMode === "day" ||
                          columnMode === "day-practitioner"
                            ? column
                            : formatDateFromDate(viewDays[0]);

                        return (
                          <TimeSlotCell
                            key={columnIndex}
                            dateString={firstDayDateString}
                            hour={timeSlot}
                            hourBookings={[]} // Legacy prop, not used when weekBookingsByDay is provided
                            hourBlockOuts={[]} // Legacy prop, not used when weekBlockOutsByDay is provided
                            serviceColors={serviceColors}
                            serviceColorsFromData={serviceColorsFromData}
                            serviceNameMap={serviceNameMap}
                            serviceColorMap={serviceColorMap}
                            onCreateBooking={onCreateBooking}
                            onCreateBookingWithDefaults={
                              onCreateBookingWithDefaults
                            }
                            onEditBooking={onShowEditBooking}
                            onDeleteBooking={onDeleteBooking}
                            onEditBlockOut={onShowEditBlockOut}
                            onEditTask={handleEditTask}
                            onStartConsultation={handleStartConsultation}
                            pharmacists={displayPharmacists}
                            rooms={roomNames}
                            size="week"
                            isDarkMode={isDarkMode}
                            onBookingDrop={handleBookingDrop}
                            onDurationChange={handleDurationChange}
                            onBlockOutDurationChange={
                              handleBlockOutDurationChange
                            }
                            isNonWorkingHours={false}
                            weekBookingsByDay={weekBookingsByDay}
                            weekBlockOutsByDay={weekBlockOutsByDay}
                            blockOutLanesByDay={blockOutLanesByDay}
                            weekTasksByDay={weekTasksByDay}
                            weekGoogleEventsByDay={weekGoogleEventsByDay}
                            weekDays={
                              columnMode === "day" ||
                              columnMode === "day-practitioner"
                                ? [new Date(`${column}T00:00:00`)]
                                : viewDays
                            }
                            nonWorkingHoursByDay={nonWorkingHoursByDay}
                            columnMode={columnMode}
                            columnValue={column}
                            currentView={view}
                            notifiedBookingId={notifiedBookingId}
                            notificationType={notificationType}
                            highlightedBookingId={highlightedAppointmentId}
                          />
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}

          {(view === "week" ||
            view === "threeday" ||
            view === "workweek" ||
            view === "day") &&
            displayMode === "list" && (
              <div
                className={`shrink-0 rounded-lg border overflow-hidden transition-colors duration-300 ${
                  isDarkMode
                    ? "bg-gray-800 border-gray-600"
                    : "bg-white border-gray-200"
                }`}
              >
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr
                        className={`border-b transition-colors duration-300 ${
                          isDarkMode
                            ? "bg-gray-700 border-gray-600"
                            : "bg-gray-50 border-gray-200"
                        }`}
                      >
                        <th
                          className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Date
                        </th>
                        <th
                          className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Time
                        </th>
                        <th
                          className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 w-48 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Patient
                        </th>
                        <th
                          className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Service Type
                        </th>
                        <th
                          className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Pharmacist
                        </th>
                        <th
                          className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Actions
                        </th>
                        <th
                          className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Vaccines Required
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(view === "week"
                        ? getWeekDays(currentDate)
                        : view === "threeday"
                          ? getThreeDays(currentDate)
                          : view === "workweek"
                            ? getWorkWeekDays(currentDate)
                            : view === "day"
                              ? [currentDate]
                              : getWeekDays(currentDate)
                      ).map((day) => {
                        const dateString = formatDateFromDate(day);
                        const dayBookings = getBookingsForDate(dateString);

                        return dayBookings.map((booking) => {
                          const matchedService = defaultPharmacyServices?.find(
                            (service) => service._id === booking.serviceId,
                          );

                          const serviceName = matchedService?.serviceName || "";
                          const serviceColor =
                            matchedService?.serviceColor ||
                            booking.serviceColor ||
                            "";

                          const bookingDate = new Date(dateString);
                          const dayOfWeek = [
                            "Sun",
                            "Mon",
                            "Tue",
                            "Wed",
                            "Thu",
                            "Fri",
                            "Sat",
                          ][bookingDate.getDay()];
                          const month = String(
                            bookingDate.getMonth() + 1,
                          ).padStart(2, "0");
                          const dayNum = String(bookingDate.getDate()).padStart(
                            2,
                            "0",
                          );
                          const formattedDate = `${dayNum}/${month} ${dayOfWeek}`;

                          const [startHour, startMin] = booking.time
                            .split(":")
                            .map(Number);
                          const endMinutes =
                            startHour * 60 + startMin + booking.duration;
                          const endHour = Math.floor(endMinutes / 60);
                          const endMin = endMinutes % 60;

                          const formatTime = (hour: number, min: number) => {
                            const period = hour >= 12 ? "pm" : "am";
                            const displayHour =
                              hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                            return min === 0
                              ? `${displayHour}${period}`
                              : `${displayHour}:${String(min).padStart(2, "0")}${period}`;
                          };

                          const timeRange = `${formatTime(startHour, startMin)}-${formatTime(endHour, endMin)}`;

                          return (
                            <tr
                              key={`${dateString}-${booking.id}`}
                              className={`border-b transition-colors duration-300 hover:${
                                isDarkMode ? "bg-gray-700" : "bg-gray-50"
                              } cursor-pointer ${
                                isDarkMode
                                  ? "border-gray-600"
                                  : "border-gray-200"
                              }`}
                              onClick={() =>
                                onShowEditBooking(booking.id, booking)
                              }
                            >
                              <td
                                className={`px-6 py-4 text-sm transition-colors duration-300 whitespace-nowrap font-medium ${
                                  isDarkMode ? "text-gray-300" : "text-gray-900"
                                }`}
                              >
                                {formattedDate}
                              </td>
                              <td
                                className={`px-6 py-4 text-sm transition-colors duration-300 whitespace-nowrap ${
                                  isDarkMode ? "text-gray-400" : "text-gray-600"
                                }`}
                              >
                                {timeRange}
                              </td>
                              <td
                                className={`px-6 py-4 text-sm transition-colors duration-300 max-w-xs ${
                                  isDarkMode ? "text-gray-300" : "text-gray-900"
                                }`}
                              >
                                <div className="truncate font-medium">
                                  {booking.patientName}
                                </div>
                              </td>
                              <td
                                className={`px-6 py-4 text-sm transition-colors duration-300 ${
                                  isDarkMode ? "text-gray-400" : "text-gray-600"
                                }`}
                              >
                                {serviceName?.trim() ? (
                                  <span
                                    className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium"
                                    style={{
                                      backgroundColor: `${serviceColor}20`,
                                      color: serviceColor,
                                    }}
                                  >
                                    <span
                                      className="w-2 h-2 rounded-full"
                                      style={{ backgroundColor: serviceColor }}
                                    />
                                    {serviceName}
                                  </span>
                                ) : (
                                  <span
                                    className={`${
                                      isDarkMode
                                        ? "text-gray-500"
                                        : "text-gray-400"
                                    }`}
                                  >
                                    None
                                  </span>
                                )}
                              </td>
                              <td
                                className={`px-6 py-4 text-sm transition-colors duration-300 ${
                                  isDarkMode ? "text-gray-400" : "text-gray-600"
                                }`}
                              >
                                {booking.pharmacist}
                              </td>
                              <td
                                className={`px-6 py-4 text-sm transition-colors duration-300 ${
                                  isDarkMode ? "text-gray-400" : "text-gray-600"
                                }`}
                              >
                                {booking.tasksToComplete &&
                                booking.tasksToComplete.length > 0 ? (
                                  <div className="flex flex-wrap gap-1">
                                    {booking.tasksToComplete.map((task) => (
                                      <span
                                        key={task.id}
                                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                                          isDarkMode
                                            ? "bg-blue-900/30 text-blue-300"
                                            : "bg-blue-100 text-blue-800"
                                        }`}
                                        title={task.label}
                                      >
                                        {task.label}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <span
                                    className={`${
                                      isDarkMode
                                        ? "text-gray-500"
                                        : "text-gray-400"
                                    }`}
                                  >
                                    None
                                  </span>
                                )}
                              </td>
                              <td
                                className={`px-6 py-4 text-sm transition-colors duration-300 ${
                                  isDarkMode ? "text-gray-400" : "text-gray-600"
                                }`}
                              >
                                {booking.vaccinesToAdminister &&
                                booking.vaccinesToAdminister.length > 0 ? (
                                  <div className="flex flex-wrap gap-1">
                                    {booking.vaccinesToAdminister.map(
                                      (vaccine) => (
                                        <span
                                          key={vaccine.id}
                                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                                            isDarkMode
                                              ? "bg-amber-900/30 text-amber-300"
                                              : "bg-amber-100 text-amber-800"
                                          }`}
                                          title={
                                            vaccine.dose
                                              ? `${vaccine.dose}`
                                              : ""
                                          }
                                        >
                                          {vaccine.name}
                                        </span>
                                      ),
                                    )}
                                  </div>
                                ) : (
                                  <span
                                    className={`${
                                      isDarkMode
                                        ? "text-gray-500"
                                        : "text-gray-400"
                                    }`}
                                  >
                                    None
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        });
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

          {false && view === "day" && displayMode === "calendar" && (
            <div
              className={`border rounded-lg overflow-hidden transition-colors duration-300 ${
                isDarkMode
                  ? "bg-gray-800 border-gray-600"
                  : "bg-white border-gray-200"
              }`}
            >
              {/* Day Header with Column Headers */}
              {/* Disabled day view - weekViewColumns reference removed to prevent compile errors */}
              {/*
              <div
                className={`grid border-b transition-colors duration-300 ${
                  isDarkMode
                    ? "bg-gray-700 border-gray-600"
                    : "bg-gray-50 border-gray-200"
                }`}
                style={{
                  gridTemplateColumns: `120px repeat(${(columnMode === "practitioner" || columnMode === "day" ? pharmacistNames : columnMode === "services" ? serviceTypes.map((s: any) => s.serviceName || s) : roomNames).length}, 1fr)`,
                }}
              >
                <div
                  className={`p-4 border-r transition-colors duration-300 ${
                    isDarkMode ? "border-gray-600" : "border-gray-200"
                  }`}
                >
                  <div
                    className={`text-lg font-medium transition-colors duration-300 ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {dayNames[currentDate.getDay()]},{" "}
                    {currentDate.toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })}
                  </div>
                </div>
                {(columnMode === "practitioner" || columnMode === "day" ? pharmacistNames : columnMode === "services" ? serviceTypes.map((s: any) => s.serviceName || s) : roomNames).map((column, index) => {
                  if (columnMode === "practitioner" || columnMode === "day") {
                    const pharmacistName = column;
                    return (
                      <div
                        key={index}
                        className={`p-4 text-center border-r transition-colors duration-300 ${
                          isDarkMode ? "border-gray-600" : "border-gray-200"
                        }`}
                      >
                        <div
                          className={`text-sm font-medium transition-colors duration-300 ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {pharmacistName}
                        </div>
                      </div>
                    );
                  } else if (columnMode === "services") {
                    const serviceType = column;
                    return (
                      <div
                        key={index}
                        className={`p-4 text-center border-r transition-colors duration-300 ${
                          isDarkMode ? "border-gray-600" : "border-gray-200"
                        }`}
                      >
                        <div
                          className={`text-sm font-medium transition-colors duration-300 ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {serviceType}
                        </div>
                      </div>
                    );
                  } else if (columnMode === "room") {
                    const roomName = column;
                    return (
                      <div
                        key={index}
                        className={`p-4 text-center border-r transition-colors duration-300 ${
                          isDarkMode ? "border-gray-600" : "border-gray-200"
                        }`}
                      >
                        <div
                          className={`text-sm font-medium transition-colors duration-300 ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {roomName}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })}
              </div>

              {/* Time Slots with Pharmacist Columns */}
              {timeSlots.map((hour) => {
                const dateString = formatDateFromDate(currentDate);
                const timeLabel = formatTimeSlotLabel(hour);

                return (
                  <div
                    key={hour}
                    className={`grid border-b transition-colors duration-300 relative overflow-visible ${
                      isDarkMode ? "border-gray-600" : "border-gray-200"
                    }`}
                    style={{
                      gridTemplateColumns: `120px repeat(${(columnMode === "practitioner" || columnMode === "day" || columnMode === "day-practitioner" ? pharmacistNames : columnMode === "services" ? serviceTypes.map((s: any) => s.serviceName || s) : roomNames).length}, 1fr)`,
                    }}
                  >
                    <div
                      className={`p-2 border-r transition-colors duration-300 ${
                        isDarkMode
                          ? "bg-gray-700 border-gray-600"
                          : "bg-gray-50 border-gray-200"
                      }`}
                    >
                      <div
                        className={`text-xs transition-colors duration-300 ${
                          isDarkMode ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        {timeLabel}
                      </div>
                    </div>

                    {weekViewColumns?.map((column, columnIndex) => {
                      const allBookings = getFilteredBookingsForTimeSlot(
                        dateString,
                        hour,
                      );
                      const allBlockOuts = getFilteredBlockOutsForTimeSlot(
                        dateString,
                        hour,
                      );
                      const allTasks = getTasksForTimeSlot(dateString, hour);

                      let hourBookings: Booking[] = [];
                      let hourBlockOuts: any[] = [];
                      let hourTasks: Task[] = [];
                      let isNonWorkingHours = false;

                      if (
                        columnMode === "practitioner" ||
                        columnMode === "day-practitioner"
                      ) {
                        const pharmacistName = column;
                        hourBookings = allBookings.filter(
                          (b) => b.pharmacist === pharmacistName,
                        );
                        hourBlockOuts = allBlockOuts.filter((bo) => {
                          const matches =
                            !bo.affectedPharmacists ||
                            bo.affectedPharmacists.length === 0 ||
                            bo.affectedPharmacists.includes(pharmacistName);
                          return matches;
                        });

                        const normP = (pharmacistName || "")
                          .trim()
                          .toLowerCase();
                        const pResource = (resources || []).find(
                          (r: any) =>
                            (r.name && r.name.trim().toLowerCase() === normP) ||
                            (r.id &&
                              String(r.id).trim().toLowerCase() === normP),
                        );
                        const pResId = (pResource?.id || "")
                          .trim()
                          .toLowerCase();

                        hourTasks = allTasks.filter((t) => {
                          const normAssigned = (t.assignedTo || "")
                            .trim()
                            .toLowerCase();
                          return (
                            normAssigned === normP ||
                            (pResId && normAssigned === pResId) ||
                            !t.assignedTo ||
                            t.assignedTo === ""
                          );
                        });
                      } else if (columnMode === "day") {
                        hourBookings = allBookings;
                        hourBlockOuts = allBlockOuts;
                        hourTasks = allTasks;
                      } else if (columnMode === "services") {
                        const serviceType = column;
                        hourBookings = allBookings.filter((b) => {
                          const bName = b.serviceId
                            ? serviceNameMap[b.serviceId]
                            : b.serviceType;
                          return (
                            b.serviceType === serviceType ||
                            b.serviceId === serviceType ||
                            (bName &&
                              bName.trim().toLowerCase() ===
                                serviceType.trim().toLowerCase())
                          );
                        });
                        hourBlockOuts = allBlockOuts;
                        hourTasks = allTasks;
                      } else if (columnMode === "room") {
                        const roomName = column;
                        hourBookings = allBookings.filter(
                          (b) => b.room === roomName,
                        );
                        hourBlockOuts = allBlockOuts.filter(
                          (bo) =>
                            !bo.affectedRooms ||
                            bo.affectedRooms.length === 0 ||
                            bo.affectedRooms.includes(roomName),
                        );
                        hourTasks = allTasks;
                      }

                      const isWorking = checkSlotWorkingStatus(
                        currentDate,
                        timeLabel,
                        columnMode,
                        column,
                        selectedPharmacists,
                        selectedRooms,
                        selectedServiceTypes,
                        resources,
                        pharmacistNames,
                        roomNames,
                      );
                      isNonWorkingHours = !isWorking;

                      return (
                        <TimeSlotCell
                          key={columnIndex}
                          dateString={dateString}
                          hour={hour}
                          hourBookings={hourBookings}
                          hourBlockOuts={hourBlockOuts}
                          hourTasks={hourTasks}
                          serviceColors={serviceColors}
                          serviceColorsFromData={serviceColorsFromData}
                          serviceNameMap={serviceNameMap}
                          serviceColorMap={serviceColorMap}
                          onCreateBooking={onCreateBooking}
                          onCreateBookingWithDefaults={
                            onCreateBookingWithDefaults
                          }
                          onEditBooking={onShowEditBooking}
                          onDeleteBooking={onDeleteBooking}
                          onEditBlockOut={onShowEditBlockOut}
                          onEditTask={handleEditTask}
                          onStartConsultation={handleStartConsultation}
                          pharmacists={pharmacistNames}
                          rooms={roomNames}
                          size="day"
                          isDarkMode={isDarkMode}
                          onBookingDrop={handleBookingDrop}
                          onDurationChange={handleDurationChange}
                          onBlockOutDurationChange={
                            handleBlockOutDurationChange
                          }
                          isNonWorkingHours={isNonWorkingHours}
                          columnMode={columnMode}
                          columnValue={column}
                          notifiedBookingId={notifiedBookingId}
                          notificationType={notificationType}
                          highlightedBookingId={highlightedAppointmentId}
                        />
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}

          {false && view === "day" && displayMode === "list" && (
            <div
              className={`rounded-lg border overflow-hidden transition-colors duration-300 ${
                isDarkMode
                  ? "bg-gray-800 border-gray-600"
                  : "bg-white border-gray-200"
              }`}
            >
              <div
                className={`p-4 border-b transition-colors duration-300 ${
                  isDarkMode
                    ? "bg-gray-700 border-gray-600"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <div
                  className={`text-lg font-medium transition-colors duration-300 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  {dayNames[currentDate.getDay()]},{" "}
                  {currentDate.toLocaleDateString()}
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr
                      className={`border-b transition-colors duration-300 ${
                        isDarkMode
                          ? "bg-gray-700 border-gray-600"
                          : "bg-gray-50 border-gray-200"
                      }`}
                    >
                      <th
                        className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Time
                      </th>
                      <th
                        className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 w-48 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Patient
                      </th>
                      <th
                        className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Service Type
                      </th>
                      <th
                        className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Pharmacist
                      </th>
                      <th
                        className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Room
                      </th>
                      <th
                        className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Actions
                      </th>
                      <th
                        className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Vaccines Required
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {getBookingsForDate(formatDateFromDate(currentDate))
                      .sort(
                        (a, b) =>
                          parseInt(a.time.split(":")[0]) -
                          parseInt(b.time.split(":")[0]),
                      )
                      .map((booking) => {
                        const [startHour, startMin] = booking.time
                          .split(":")
                          .map(Number);
                        const endMinutes =
                          startHour * 60 + startMin + booking.duration;
                        const endHour = Math.floor(endMinutes / 60);
                        const endMin = endMinutes % 60;

                        const formatTime = (hour: number, min: number) => {
                          const period = hour >= 12 ? "pm" : "am";
                          const displayHour =
                            hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                          return min === 0
                            ? `${displayHour}${period}`
                            : `${displayHour}:${String(min).padStart(2, "0")}${period}`;
                        };

                        const timeRange = `${formatTime(startHour, startMin)}-${formatTime(endHour, endMin)}`;

                        return (
                          <tr
                            key={booking.id}
                            className={`border-b transition-colors duration-300 hover:${
                              isDarkMode ? "bg-gray-700" : "bg-gray-50"
                            } cursor-pointer ${
                              isDarkMode ? "border-gray-600" : "border-gray-200"
                            }`}
                            onClick={() =>
                              onShowEditBooking(booking.id, booking)
                            }
                          >
                            <td
                              className={`px-6 py-4 text-sm transition-colors duration-300 whitespace-nowrap ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              {timeRange}
                            </td>
                            <td
                              className={`px-6 py-4 text-sm transition-colors duration-300 max-w-xs ${
                                isDarkMode ? "text-gray-300" : "text-gray-900"
                              }`}
                            >
                              <div className="truncate font-medium">
                                {booking.patientName}
                              </div>
                            </td>
                            <td
                              className={`px-6 py-4 text-sm transition-colors duration-300 ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              {serviceName?.trim() ? (
                                <span
                                  className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium"
                                  style={{
                                    backgroundColor: `${serviceColor}20`,
                                    color: serviceColor,
                                  }}
                                >
                                  <span
                                    className="w-2 h-2 rounded-full"
                                    style={{ backgroundColor: serviceColor }}
                                  />
                                  {serviceName}
                                </span>
                              ) : (
                                <span
                                  className={`${
                                    isDarkMode
                                      ? "text-gray-500"
                                      : "text-gray-400"
                                  }`}
                                >
                                  None
                                </span>
                              )}
                            </td>
                            <td
                              className={`px-6 py-4 text-sm transition-colors duration-300 ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              {booking.pharmacist}
                            </td>
                            <td
                              className={`px-6 py-4 text-sm transition-colors duration-300 ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              {booking.room &&
                              !String(booking.room).match(/^[0-9a-fA-F]{24}$/)
                                ? booking.room
                                : "-"}
                            </td>
                            <td
                              className={`px-6 py-4 text-sm transition-colors duration-300 ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              {booking.tasksToComplete &&
                              booking.tasksToComplete.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {booking.tasksToComplete.map((task) => (
                                    <span
                                      key={task.id}
                                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                                        isDarkMode
                                          ? "bg-blue-900/30 text-blue-300"
                                          : "bg-blue-100 text-blue-800"
                                      }`}
                                      title={task.label}
                                    >
                                      {task.label}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span
                                  className={`${
                                    isDarkMode
                                      ? "text-gray-500"
                                      : "text-gray-400"
                                  }`}
                                >
                                  None
                                </span>
                              )}
                            </td>
                            <td
                              className={`px-6 py-4 text-sm transition-colors duration-300 ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              {booking.vaccinesToAdminister &&
                              booking.vaccinesToAdminister.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {booking.vaccinesToAdminister.map(
                                    (vaccine) => (
                                      <span
                                        key={vaccine.id}
                                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                                          isDarkMode
                                            ? "bg-amber-900/30 text-amber-300"
                                            : "bg-amber-100 text-amber-800"
                                        }`}
                                        title={
                                          vaccine.dose ? `${vaccine.dose}` : ""
                                        }
                                      >
                                        {vaccine.name}
                                      </span>
                                    ),
                                  )}
                                </div>
                              ) : (
                                <span
                                  className={`${
                                    isDarkMode
                                      ? "text-gray-500"
                                      : "text-gray-400"
                                  }`}
                                >
                                  None
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Pharmacies-filter refresh in flight - overlay only this calendar
              area (not the header/filters bar) with a pulsing skeleton,
              leaving the Filters popover open and the rest of the page usable. */}
          {isScheduleEventsByPharmaciesLoading && (
            <div
              className={`absolute inset-0 z-30 flex flex-col gap-2 p-4 overflow-hidden animate-pulse ${
                isDarkMode ? "bg-gray-900/70" : "bg-white/70"
              }`}
            >
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={`pharmacies-filter-loading-row-${i}`}
                  className={`h-12 rounded-md ${
                    isDarkMode ? "bg-gray-800" : "bg-gray-200"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Calculate service color for modal */}
      {(() => {
        let modalServiceColor = "#9ca3af"; // Default gray
        const booking = modalState.booking;

        // Use the service color directly from the booking (now stored during transformation)
        if (booking?.serviceColor) {
          modalServiceColor = booking.serviceColor;
        }

        return (
          <>
            {/* Booking Modal */}
            <BookingModal
              mode={modalState.mode as "create" | "edit"}
              booking={modalState.booking as any}
              isCalendarEdit={modalState.mode === "edit"}
              isOpen={modalState.isOpen}
              onSave={onSaveFromModal}
              onDelete={
                modalState.mode === "edit" && modalState.booking
                  ? onDeleteBooking
                  : undefined
              }
              onOpenChange={(open) => {
                if (!open) closeModal();
              }}
              onCancel={closeModal}
              initialDate={modalState.initialDate}
              initialTime={modalState.initialTime}
              initialPharmacistName={modalState.initialPharmacist}
              initialServiceTypeId={(() => {
                if (!modalState.initialServiceType) return undefined;
                const found = (defaultPharmacyServices || []).find(
                  (s: any) =>
                    s._id === modalState.initialServiceType ||
                    s.serviceName?.trim().toLowerCase() ===
                      String(modalState.initialServiceType)
                        .trim()
                        .toLowerCase(),
                );
                return found ? found._id : modalState.initialServiceType;
              })()}
              serviceTypes={serviceTypes.map((s: any) => s._id || s.id || s)}
              pharmacists={pharmacistNames}
              rooms={roomNames}
              isDarkMode={isDarkMode}
              isReadOnly={false}
              serviceColor={modalServiceColor}
              onStartConsultation={handleStartConsultation}
            />
          </>
        );
      })()}

      {/* Block Out Edit Modal */}
      <BlockOutModal
        trigger={<div />}
        onSave={onSaveFromBlockOutModal}
        onDelete={
          blockOutModalState.mode === "edit" ? onDeleteBlockOut : undefined
        }
        editMode={
          blockOutModalState.mode === "edit"
            ? blockOutModalState.blockOut
            : undefined
        }
        initialDate={blockOutModalState.initialDate}
        isDarkMode={isDarkMode}
        open={blockOutModalState.isOpen}
        onOpenChange={(open) => {
          if (!open) closeBlockOutModal();
        }}
        pharmacists={pharmacistNames}
        roomNames={dynamicRooms}
        rooms={resources}
      />

      {/* Block Out Modal from Add Dropdown */}
      <BlockOutModal
        trigger={<div />}
        onSave={onCreateBlockOut}
        isDarkMode={isDarkMode}
        open={showBlockOutModal}
        onOpenChange={setShowBlockOutModal}
        pharmacists={pharmacistNames}
        roomNames={dynamicRooms}
        rooms={resources}
      />

      {/* Task Modal from Add Dropdown */}
      <TaskModal
        open={showTaskModal}
        onOpenChange={(open) => {
          setShowTaskModal(open);
          if (!open) {
            setEditingTask(null);
          }
        }}
        onSave={handleSaveTask}
        initialData={editingTask || undefined}
        isDarkMode={isDarkMode}
        pharmacists={pharmacistNames}
        dynamicResources={dynamicResources}
        currentPharmacyId={currentPharmacyId}
      />

      {/* Conflict Warning Modal */}
      {Boolean(conflictWarning) && (
        <Dialog
          open={true}
          onOpenChange={(open) => {
            if (!open) clearConflictWarning();
          }}
        >
          <DialogContent
            className={`${isDarkMode ? "bg-gray-800 border-gray-600 text-white" : "bg-white border-gray-200 text-gray-900"}`}
          >
            <DialogHeader>
              <DialogTitle
                className={`${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Action Not Allowed
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p
                className={`${isDarkMode ? "text-gray-200" : "text-gray-700"}`}
              >
                {conflictWarning}
              </p>
              <div className="flex justify-end">
                <UIButton
                  onClick={clearConflictWarning}
                  className="bg-slate-900 hover:bg-slate-800"
                >
                  OK
                </UIButton>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Reschedule Confirmation Modal */}
      <Dialog
        open={Boolean(rescheduleData)}
        onOpenChange={(open) => !open && handleCancelReschedule()}
      >
        <DialogContent
          className={`max-w-md ${isDarkMode ? "bg-gray-800 border-gray-600 text-white" : "bg-white border-gray-200 text-gray-900"}`}
        >
          <DialogHeader>
            <DialogTitle
              className={`text-xl font-semibold flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              <svg
                className="w-6 h-6 text-amber-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              Reschedule Appointment
            </DialogTitle>
          </DialogHeader>
          <div className="relative">
            <div
              className={`space-y-4 py-4 transition-[filter,opacity] duration-200 ${
                isCheckingRescheduleAvailability
                  ? "blur-[2px] opacity-60 pointer-events-none"
                  : ""
              }`}
            >
              {/* Notify Patient Toggle */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label
                    htmlFor="notify-patient"
                    className={`text-sm font-medium ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Notify Patient
                  </Label>
                  <p
                    className={`text-xs ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Send notification about the reschedule
                  </p>
                </div>
                <Switch
                  id="notify-patient"
                  checked={notifyPatient}
                  onCheckedChange={setNotifyPatient}
                />
              </div>

              {rescheduleData && (
                <div
                  className={`p-4 rounded-lg ${
                    isDarkMode ? "bg-gray-700" : "bg-gray-50"
                  }`}
                >
                  <h4
                    className={`font-medium mb-3 ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Appointment Details
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span
                        className={
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }
                      >
                        Patient:
                      </span>
                      <span
                        className={isDarkMode ? "text-white" : "text-gray-900"}
                      >
                        {rescheduleData.booking.patientName}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span
                        className={
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }
                      >
                        Service:
                      </span>
                      <span
                        className={isDarkMode ? "text-white" : "text-gray-900"}
                      >
                        {rescheduleData.booking.serviceId
                          ? serviceNameMap[rescheduleData.booking.serviceId] ||
                            rescheduleData.booking.serviceType
                          : rescheduleData.booking.serviceType}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span
                        className={
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }
                      >
                        Time:
                      </span>
                      <span
                        className={isDarkMode ? "text-white" : "text-gray-900"}
                      >
                        {rescheduleData.booking.time}
                      </span>
                    </div>
                    <div className="my-3 border-t border-dashed pt-3">
                      <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 7l5 5m0 0l-5 5m5-5H6"
                          />
                        </svg>
                        <span className="font-medium">Moving to:</span>
                      </div>
                    </div>
                    <div className="flex justify-between">
                      <span
                        className={
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }
                      >
                        New Date:
                      </span>
                      <span
                        className={`font-medium ${
                          isDarkMode ? "text-green-400" : "text-green-600"
                        }`}
                      >
                        {new Date(rescheduleData.newDate).toLocaleDateString(
                          "en-US",
                          {
                            weekday: "short",
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          },
                        )}
                      </span>
                    </div>
                    {rescheduleData.newTime && (
                      <div className="flex justify-between">
                        <span
                          className={
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }
                        >
                          New Time:
                        </span>
                        <span
                          className={`font-medium ${
                            isDarkMode ? "text-green-400" : "text-green-600"
                          }`}
                        >
                          {rescheduleData.newTime}
                        </span>
                      </div>
                    )}
                    {rescheduleData.newPharmacist &&
                      rescheduleData.newPharmacist !==
                        rescheduleData.booking.pharmacist && (
                        <div className="flex justify-between">
                          <span
                            className={
                              isDarkMode ? "text-gray-400" : "text-gray-600"
                            }
                          >
                            New Pharmacist:
                          </span>
                          <span
                            className={`font-medium ${
                              isDarkMode ? "text-green-400" : "text-green-600"
                            }`}
                          >
                            {rescheduleData.newPharmacist}
                          </span>
                        </div>
                      )}
                    {rescheduleData.newRoom &&
                      rescheduleData.newRoom !==
                        rescheduleData.booking.room && (
                        <div className="flex justify-between">
                          <span
                            className={
                              isDarkMode ? "text-gray-400" : "text-gray-600"
                            }
                          >
                            New Room:
                          </span>
                          <span
                            className={`font-medium ${
                              isDarkMode ? "text-green-400" : "text-green-600"
                            }`}
                          >
                            {rescheduleData.newRoom}
                          </span>
                        </div>
                      )}
                  </div>
                </div>
              )}
            </div>

            {isCheckingRescheduleAvailability && (
              <div className="absolute inset-0 z-10 flex items-center justify-center">
                <div
                  className={`flex items-center gap-2 rounded-md border px-4 py-3 text-sm font-medium shadow-sm ${
                    isDarkMode
                      ? "border-gray-600 bg-gray-800/95 text-gray-100"
                      : "border-gray-200 bg-white/95 text-gray-700"
                  }`}
                >
                  <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                  Checking availability...
                </div>
              </div>
            )}
          </div>

          {rescheduleConflictMessage && (
            <div
              className={`p-3 rounded-md border flex items-center gap-2 text-sm font-medium ${
                isDarkMode
                  ? "bg-red-900/30 border-red-700 text-red-200"
                  : "bg-red-50 border-red-200 text-red-700"
              }`}
            >
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{rescheduleConflictMessage}</span>
            </div>
          )}

          <div className="flex gap-3 justify-end pt-4">
            <UIButton
              variant="outline"
              onClick={handleCancelReschedule}
              disabled={isSubmittingReschedule}
              className={
                isDarkMode
                  ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                  : ""
              }
            >
              Cancel
            </UIButton>
            <UIButton
              onClick={handleConfirmReschedule}
              disabled={
                isSubmittingReschedule ||
                isCheckingRescheduleAvailability ||
                Boolean(rescheduleConflictMessage)
              }
              className={
                isSubmittingReschedule || rescheduleConflictMessage
                  ? "bg-gray-400 cursor-not-allowed text-white opacity-60"
                  : "bg-gray-900 hover:bg-gray-800 text-white"
              }
            >
              {isSubmittingReschedule ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Rescheduling...
                </span>
              ) : notifyPatient ? (
                "Confirm & Notify Patient"
              ) : (
                "Confirm Reschedule"
              )}
            </UIButton>
          </div>
        </DialogContent>
      </Dialog>

      {/* Calendar Sync Modal */}
      <CalendarSyncModal
        open={showSyncModal}
        onOpenChange={setShowSyncModal}
        isDarkMode={isDarkMode}
        onSyncSuccess={() => {
          if (currentPharmacyId) {
            refreshScheduleEventsSilently(currentPharmacyId);
          }
        }}
      />

      {/* Save Default View Bar */}
      {showSaveDefaultBar && (
        <SaveDefaultViewBar
          isDarkMode={isDarkMode}
          onSave={handleSaveDefaultView}
          onDismiss={handleDismissSaveBar}
          isSaving={isSavingDefaultView}
        />
      )}
    </div>
  );
}
